import jsPDF from "jspdf";
import QRCode from "qrcode";
import { formatDate, isInventoryItem } from "./utils-app";
import { ADAMS_LOGO } from "./logo";

// ────────────────────────────────────────────────────────────────
// Save-with-share helper — Round 124.19
// Every PDF that is generated client-side flows through `savePdf`.
// On mobile devices that expose the Web Share API with file support
// (Chrome / Safari on iOS 16+ / most Android browsers), we open the
// native share sheet so users can hit WhatsApp / Gmail / etc. in one
// tap. On desktops we fall back to the classic download.
// ────────────────────────────────────────────────────────────────
const _isMobileUA = () =>
  typeof navigator !== "undefined" &&
  /Android|iPhone|iPad|iPod|Mobile|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || "");

export async function savePdf(doc, filename) {
  const safeName = filename && filename.endsWith(".pdf") ? filename : `${filename || "document"}.pdf`;
  try {
    if (_isMobileUA() && typeof navigator !== "undefined" && navigator.share && navigator.canShare) {
      const blob = doc.output("blob");
      const file = new File([blob], safeName, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: safeName });
        return;
      }
    }
  } catch (err) {
    // If share fails or is dismissed, fall through to `doc.save()`.
    if (err && err.name === "AbortError") return;
  }
  doc.save(safeName);
}

// Same idea for server-generated PDF blobs (e.g. procurement PO PDF)
// or any downloaded attachment (invoice image, receipt PDF, etc.).
export async function shareOrDownloadPdfBlob(blob, filename, mime) {
  const safeName = filename || "download";
  const type = mime || blob?.type || "application/octet-stream";
  try {
    if (_isMobileUA() && typeof navigator !== "undefined" && navigator.share && navigator.canShare) {
      const file = new File([blob], safeName, { type });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: safeName });
        return;
      }
    }
  } catch (err) {
    if (err && err.name === "AbortError") return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

const COMPANY = {
  name: "ADAMS",
  tagline: "Events · Rental Services · Furniture Hirers · Decorators",
  address:
    "No. 237/238/239, Narayan Pillai Street, Bharati Nagar, Shivaji Nagar, Bengaluru - 560001, Karnataka.",
  gst: "29AAVPA0474D1ZQ",
  sac: "997323 / 997329 / 996519",
  web: "www.adamsbangalore.com",
  email: "events@adamsbangalore.com",
};

// Generate a PNG data-URL for the order's permalink. Used in challans
// so the customer (or the warehouse) can scan to open the premium
// tracking dashboard. `kind` is encoded as `?type=delivery|return` so
// the dashboard knows which trip to highlight for the customer.
async function _orderQrDataUrl(order, kind = "delivery") {
  try {
    const id = order.id || order.order_number || "";
    const typeParam = kind === "return" ? "return" : "delivery";
    const url = `${window.location.origin}/client/${encodeURIComponent(id)}?type=${typeParam}`;
    return await QRCode.toDataURL(url, {
      margin: 0,
      width: 320,
      errorCorrectionLevel: "M",
      color: { dark: "#0f172a", light: "#ffffff" },
    });
  } catch {
    return null;
  }
}

function renderChallan(doc, order, { firstPage, overrideChallanNumber, kind, trip, qrDataUrl } = { firstPage: true, kind: "delivery" }) {
  const isReturn = kind === "return";
  if (!firstPage) doc.addPage();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const BLACK = [17, 24, 39];
  const SLATE = [71, 85, 105];
  const HAIR = [203, 213, 225];

  // ─── Top tagline (plain centred text — NO black strip) ────────────
  doc.setTextColor(...SLATE);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text(COMPANY.tagline, pageW / 2, 22, { align: "center" });

  // ─── Header — logo (left) + address block (right) ─────────────────
  const headerTop = 32;
  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, headerTop, 175, 78);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(...BLACK);
    doc.text("ADAMS", 40, headerTop + 36);
  }
  // Right-side address block (slightly larger so dispatch staff can read
  // GSTIN / SAC / address from a meter away on the dock).
  doc.setTextColor(...BLACK);
  const rightX = pageW - 40;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("ADAMS", rightX, headerTop + 12, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Registered Office", rightX, headerTop + 28, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const addressLines = doc.splitTextToSize(COMPANY.address, 290);
  doc.text(addressLines, rightX, headerTop + 41, { align: "right" });
  let ry = headerTop + 41 + addressLines.length * 11 + 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(`GSTIN  ${COMPANY.gst}`, rightX, ry, { align: "right" });
  ry += 11;
  doc.text(`SAC  ${COMPANY.sac}`, rightX, ry, { align: "right" });

  // Single hairline divider
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(1);
  doc.line(40, 116, pageW - 40, 116);

  // ─── Title row ────────────────────────────────────────────────────
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  const title = isReturn ? "RETURN MEMO" : "DELIVERY CHALLAN";
  doc.text(title, 40, 142);
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(1.5);
  doc.line(40, 148, 40 + doc.getTextWidth(title), 148);

  // Differentiator — pale grey "RETURN" watermark for B&W safety
  if (isReturn) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(40);
    doc.setTextColor(229, 231, 235);
    doc.text("RETURN", pageW - 40, 152, { align: "right" });
  }

  // Challan number — outlined card top-right (no fill)
  const baseChallan = overrideChallanNumber || order.challan_number || "—";
  const challanNo = isReturn
    ? (order.return_challan_number || baseChallan.replace(/^CH-/, "RCH-"))
    : baseChallan;
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.8);
  doc.roundedRect(pageW - 200, 128, 160, 26, 4, 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...SLATE);
  doc.text(isReturn ? "RETURN MEMO NO." : "CHALLAN NO.", pageW - 192, 140);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...BLACK);
  doc.text(String(challanNo), pageW - 192, 150);

  // ─── Compact key-date callout — clean outlined, no fill ──────────
  // We intentionally keep this monochrome so the printed PDF stays
  // crisp on B&W laser printers. Visual highlighting of the key date
  // happens on the live Track page (where the customer lands after
  // scanning the QR), not on the PDF itself.
  let y = 168;
  const dateBoxH = 44;
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(1.2);
  doc.roundedRect(40, y, pageW - 80, dateBoxH, 6, 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...SLATE);
  doc.text(isReturn ? "RETURN PICKUP ON" : "DELIVER ON", 52, y + 16);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BLACK);
  const keyDate = isReturn ? order.return_date : order.delivery_date;
  doc.text(keyDate ? formatDate(keyDate) : "—", 52, y + 36);

  // Right-side metadata grid
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SLATE);
  const rightInfoX = pageW - 230;
  doc.text("Order No.", rightInfoX, y + 14);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BLACK);
  doc.text(String(order.order_number || "—"), rightInfoX + 60, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SLATE);
  doc.text("Event Date", rightInfoX, y + 28);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BLACK);
  doc.text(order.event_date ? formatDate(order.event_date) : "—", rightInfoX + 60, y + 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SLATE);
  doc.text(isReturn ? "Delivered" : "Return", rightInfoX, y + 40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BLACK);
  doc.text(
    isReturn
      ? (order.delivery_date ? formatDate(order.delivery_date) : "—")
      : (order.return_date ? formatDate(order.return_date) : "—"),
    rightInfoX + 60,
    y + 40,
  );
  y += dateBoxH + 20;

  // ─── Customer block — generous space, bigger phone & address ─────
  // Address & phone are read on the dock from a few feet away so we
  // give them room to breathe + larger font.
  const custBaseH = 96;
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.8);
  // Pre-compute address line count so the rounded box wraps the text.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const addrLines = doc.splitTextToSize(`Address:  ${order.address || "—"}`, pageW - 110);
  // Optional Customer GSTIN — printed verbatim below the address (no
  // "Notes" section on the PDF anymore; remarks/notes are an internal
  // ops field only). Empty/missing GSTIN omits the line entirely.
  const gstinText = (order.customer_gstin || "").trim();
  const gstinLines = gstinText
    ? [`GSTIN:  ${gstinText.toUpperCase()}`]
    : [];
  const gstinBlockH = gstinLines.length > 0 ? 8 + gstinLines.length * 14 + 6 : 0;
  const dynamicCustH = Math.max(
    custBaseH,
    80 + addrLines.length * 16 + 12 + gstinBlockH
  );
  doc.roundedRect(40, y, pageW - 80, dynamicCustH, 6, 6);
  // Section label
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...SLATE);
  doc.text(isReturn ? "PICKUP FROM" : "DELIVER TO", 52, y + 14);
  // Customer name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...BLACK);
  doc.text(order.customer_name || "—", 52, y + 34);
  // Phone (bigger)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.text(`Phone:  ${order.customer_phone || "—"}`, 52, y + 56);
  // Address (bigger, more line height)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(addrLines, 52, y + 78, { lineHeightFactor: 1.25 });
  // GSTIN (if present) — same visual treatment as Phone (normal, 13pt,
  // black) so the panel reads as a clean stack of equally-weighted
  // shipping fields. Sits inside the same rounded card.
  if (gstinLines.length > 0) {
    const gY = 78 + addrLines.length * 16 + 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.setTextColor(...BLACK);
    doc.text(gstinLines, 52, y + gY, { lineHeightFactor: 1.25 });
  }
  y += dynamicCustH + 20;

  // ─── Dispatch (DC only) — compact outlined cells ─────────────────
  if (!isReturn) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...SLATE);
    doc.text("DISPATCH INFORMATION", 40, y);
    y += 10;
    const cellH = 30;
    const cells = [
      { label: "Driver Name", value: trip?.driverName || "" },
      { label: "Labour Name", value: trip?.labourName || "" },
      { label: "Vehicle No.", value: trip?.vehicle || "" },
      { label: "Dispatch Time", value: _pdfFormatTime(trip?.dispatchTime) },
    ];
    const cw = (pageW - 80) / cells.length;
    cells.forEach((c, i) => {
      const x = 40 + i * cw;
      doc.setDrawColor(...HAIR);
      doc.setLineWidth(0.8);
      doc.roundedRect(x + 2, y, cw - 4, cellH, 3, 3);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...SLATE);
      doc.text(c.label.toUpperCase(), x + 8, y + 11);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BLACK);
      doc.text(c.value || " ", x + 8, y + 24);
    });
    y += cellH + 22;
  }

  // ─── HERO: Items table ───────────────────────────────────────────
  const items = (order.items || []).filter((it) => isInventoryItem(it.name));

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...BLACK);
  doc.text(isReturn ? "ITEMS TO COLLECT" : "ITEMS TO DELIVER", 40, y);
  y += 16;

  // Outlined header row (NO black fill — just thin outlined cells)
  // Two columns only: Qty (left, narrow) | Item Description (wide).
  const headerY = y;
  const headerH = 24;
  const colQtyL = 40;
  const colQtyW = 70;
  const colItem = colQtyL + colQtyW; // 110
  const colRight = pageW - 40;
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(0.8);
  doc.line(40, headerY, pageW - 40, headerY);            // top
  doc.line(40, headerY + headerH, pageW - 40, headerY + headerH); // bottom
  // Vertical separator between Qty and Item Description
  doc.line(colItem, headerY, colItem, headerY + headerH);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BLACK);
  doc.text("Qty", colQtyL + colQtyW / 2, headerY + 16, { align: "center" });
  doc.text("Item Description", colItem + 8, headerY + 16);
  y += headerH;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...BLACK);
  items.forEach((it, i) => {
    // Push to 720pt (signature line lives at pageH-110 ≈ 732); keeps a
    // hair of safety margin while letting ~5 more rows fit per page than
    // the old 660pt cutoff.
    if (y > 720) {
      doc.addPage();
      y = 60;
    }
    const nameLines = doc.splitTextToSize(it.name || "-", colRight - colItem - 16);
    // Tighter row: 18pt floor (was 22) + 13pt per wrap line (was 14) — no
    // font-size reduction, just trimmed vertical padding around the
    // baseline. Net effect: ~20% more rows per page on dense challans.
    const rowH = Math.max(18, nameLines.length * 13 + 5);
    // Subtle alternating tint — very light grey, prints faint
    if (i % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(40, y, pageW - 80, rowH, "F");
    }
    // Vertical separator between qty and item
    doc.setDrawColor(...HAIR);
    doc.setLineWidth(0.5);
    doc.line(colItem, y, colItem, y + rowH);
    // Bottom hairline per row
    doc.line(40, y + rowH, pageW - 40, y + rowH);
    // Content
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BLACK);
    // Center text vertically within the (variable) rowH so the dense
    // 18pt rows still look balanced and the multi-line wrapped rows
    // don't bunch their text against the top.
    const textY = nameLines.length === 1 ? y + Math.min(13, rowH - 5) : y + 12;
    doc.text(String(it.quantity || 1), colQtyL + colQtyW / 2, textY, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(nameLines, colItem + 8, textY);
    y += rowH;
  });
  // Outer left/right verticals from title to bottom
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(0.8);
  doc.line(40, headerY, 40, y);
  doc.line(pageW - 40, headerY, pageW - 40, y);
  y += 20;

  // ─── Return-only — Damaged / Missing inspection (compact) ────────
  if (isReturn) {
    const blockH = 78;
    if (y + blockH > pageH - 110) {
      doc.addPage();
      y = 60;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...SLATE);
    doc.text("INSPECTION ON RETURN", 40, y);
    y += 10;
    const halfW = (pageW - 80 - 12) / 2;
    // Damage panel
    doc.setDrawColor(...HAIR);
    doc.setLineWidth(0.8);
    doc.roundedRect(40, y, halfW, blockH, 5, 5);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...BLACK);
    doc.text("DAMAGED ITEMS", 50, y + 14);
    doc.setDrawColor(...HAIR);
    for (let i = 1; i <= 3; i++) {
      doc.line(50, y + 22 + i * 16, 40 + halfW - 10, y + 22 + i * 16);
    }
    // Missing panel
    const mx = 40 + halfW + 12;
    doc.setDrawColor(...HAIR);
    doc.roundedRect(mx, y, halfW, blockH, 5, 5);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...BLACK);
    doc.text("MISSING ITEMS", mx + 10, y + 14);
    doc.setDrawColor(...HAIR);
    for (let i = 1; i <= 3; i++) {
      doc.line(mx + 10, y + 22 + i * 16, mx + halfW - 10, y + 22 + i * 16);
    }
    y += blockH + 12;
  }

  // ─── Signatures (anchored bottom) ────────────────────────────────
  const sigY = pageH - 110;
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.8);
  const colWidth = (pageW - 80) / 3;
  for (let c = 0; c < 3; c++) {
    const x1 = 40 + c * colWidth + 15;
    const x2 = 40 + (c + 1) * colWidth - 15;
    doc.line(x1, sigY, x2, sigY);
  }
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BLACK);
  const labels = ["Driver Signature", "Godown Supervisor", "Customer Signature"];
  labels.forEach((lbl, c) => {
    const center = 40 + c * colWidth + colWidth / 2;
    doc.text(lbl, center, sigY + 12, { align: "center" });
  });

  // ─── Footer (Round 60 — compact, printer-safe) ───────────────────
  // QR sits in the bottom-left, caption next to it on the same baseline,
  // company web/email centered below. All elements ≥22pt from the page
  // edge so no laser/inkjet printer cuts off the QR.
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.5);
  doc.line(40, pageH - 74, pageW - 40, pageH - 74);

  if (qrDataUrl) {
    try {
      doc.addImage(qrDataUrl, "PNG", 40, pageH - 68, 36, 36);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...SLATE);
      doc.text("Scan to track order", 82, pageH - 52);
    } catch (e) {
      /* ignore */
    }
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BLACK);
  doc.text(
    `${COMPANY.web}  ·  ${COMPANY.email}`,
    pageW / 2,
    pageH - 22,
    { align: "center" },
  );
}

// Pull saved trip data straight off the order. Used to auto-fill the
// dispatch band on the Delivery Challan when the planner has already
// assigned a vehicle/driver/dispatch — no need to retype on the PDF.
function _tripFromOrder(order, kind = "delivery") {
  if (!order) return {};
  const px = kind === "return" ? "return" : "delivery";
  const v = order[`${px}_vehicle_no`] || (order.trip_kind === kind ? order.vehicle_no : "") || "";
  const d = order[`${px}_driver_name`] || (order.trip_kind === kind ? order.driver_name : "") || "";
  const t = order[`${px}_dispatch_time`] || (order.trip_kind === kind ? order.dispatch_time : "") || "";
  return { vehicle: v, driverName: d, dispatchTime: t };
}

export async function downloadChallanPDF(order) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const qrDataUrl = await _orderQrDataUrl(order, "delivery");
  // Auto-fill vehicle/driver/dispatch from the saved trip plan if any —
  // staff no longer has to retype these onto the printed challan.
  const trip = _tripFromOrder(order, "delivery");
  renderChallan(doc, order, { firstPage: true, kind: "delivery", qrDataUrl, trip });
  const safeName = (order.customer_name || "challan").replace(/[^a-z0-9]+/gi, "_");
  savePdf(doc, `Challan-${safeName}.pdf`);
}

export async function downloadReturnChallanPDF(order) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const qrDataUrl = await _orderQrDataUrl(order, "return");
  const trip = _tripFromOrder(order, "return");
  renderChallan(doc, order, { firstPage: true, kind: "return", qrDataUrl, trip });
  const safeName = (order.customer_name || "return").replace(/[^a-z0-9]+/gi, "_");
  savePdf(doc, `Return-Memo-${safeName}.pdf`);
}

function _combinedChallanNumber() {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let suffix = "";
  for (let i = 0; i < 4; i++) {
    suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `CH-MLT-${yy}${mm}${dd}-${suffix}`;
}

function renderVehicleManifest(doc, entries, manifestNo, trip = {}) {
  const pageW = doc.internal.pageSize.getWidth();
  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 26, 205, 94);
  } catch (e) {
    /* ignore */
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  const rightX = pageW - 40;
  let ry = 42;
  doc.text("Registered Office", rightX, ry, { align: "right" });
  ry += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const addrLines = doc.splitTextToSize(COMPANY.address, 280);
  doc.text(addrLines, rightX, ry, { align: "right" });
  ry += addrLines.length * 12 + 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`GSTIN: ${COMPANY.gst}`, rightX, ry, { align: "right" });
  ry += 12;
  doc.text(`SAC: ${COMPANY.sac}`, rightX, ry, { align: "right" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 125, pageW - 40, 125);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(17, 24, 39);
  doc.text("COMBINED VEHICLE MANIFEST", pageW / 2, 152, { align: "center" });
  doc.setFontSize(10);
  doc.setTextColor(220, 38, 38);
  doc.text(`Combined Challan No: ${manifestNo}`, pageW / 2, 170, { align: "center" });
  doc.setTextColor(107, 114, 128);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const delCount = entries.filter((e) => e.kind === "delivery").length;
  const retCount = entries.filter((e) => e.kind === "return").length;
  doc.text(
    `${entries.length} stops · ${delCount} ${delCount === 1 ? "delivery" : "deliveries"} · ${retCount} ${retCount === 1 ? "pickup" : "pickups"}`,
    pageW / 2,
    184,
    { align: "center" }
  );

  // Vehicle/driver/dispatch band
  let y = 206;
  if (trip.vehicle || trip.driverName || trip.dispatchTime) {
    doc.setFillColor(15, 23, 42); // slate-900
    doc.rect(40, y, pageW - 80, 42, "F");
    doc.setTextColor(203, 213, 225); // slate-300
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("VEHICLE", 52, y + 14);
    doc.text("DRIVER", 200, y + 14);
    doc.text("DISPATCH TIME", 360, y + 14);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text(String(trip.vehicle || "—"), 52, y + 32);
    doc.text(String(trip.driverName || "—"), 200, y + 32);
    doc.text(String(_pdfFormatTime(trip.dispatchTime) || "—"), 360, y + 32);
    doc.setTextColor(17, 24, 39);
    y += 56;
  } else {
    y = 220;
  }

  // Drops list
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Trip Sequence (follow in order)", 40, y);
  y += 14;
  doc.setFillColor(248, 250, 252);
  doc.rect(40, y, pageW - 80, 22, "F");
  doc.setFontSize(9);
  doc.text("#", 48, y + 15);
  doc.text("Type", 70, y + 15);
  doc.text("Customer", 130, y + 15);
  doc.text("Challan", 280, y + 15);
  doc.text("Phone", 370, y + 15);
  doc.text("Area", 460, y + 15);
  y += 22;
  doc.setFont("helvetica", "normal");
  entries.forEach((entry, i) => {
    if (y > 730) {
      doc.addPage();
      y = 60;
    }
    const o = entry.order;
    const isR = entry.kind === "return";
    // Wrap multi-number phone strings inside the Phone column instead of
    // letting them overflow into Area. We size the Phone column to end
    // ~5pt before the Area column starts (460), so a 2-line phone fits
    // neatly without colliding.
    const phoneRaw = String(o.customer_phone || "—");
    // Normalise common separators to a comma-space so jsPDF wraps cleanly
    const phoneNormalized = phoneRaw.replace(/[\/|;]+/g, ", ").replace(/,\s*/g, ", ");
    const phoneLines = doc.splitTextToSize(phoneNormalized, 460 - 370 - 5);
    const addrParts = (o.address || "").split(",").map((s) => s.trim()).filter(Boolean);
    const area = addrParts.length >= 2 ? addrParts[addrParts.length - 2] : (addrParts[0] || "—");
    // Row height grows with phone wrapping (also lets Area wrap a bit
    // more freely below if the customer is double-named).
    const rowH = Math.max(20, phoneLines.length * 12 + 8);

    if (isR) {
      doc.setFillColor(243, 232, 255);
      doc.setTextColor(126, 34, 206);
    } else {
      doc.setFillColor(254, 226, 226);
      doc.setTextColor(220, 38, 38);
    }
    doc.roundedRect(68, y + 4, 50, 14, 3, 3, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(isR ? "PICKUP" : "DELIVER", 93, y + 14, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(17, 24, 39);
    doc.text(String(i + 1), 48, y + 15);
    doc.text(String(o.customer_name || "—").slice(0, 28), 130, y + 15);
    const cn = isR
      ? (o.return_challan_number || (o.challan_number || "").replace(/^CH-/, "RCH-"))
      : o.challan_number || "—";
    doc.text(String(cn), 280, y + 15);
    doc.text(phoneLines, 370, y + 15, { lineHeightFactor: 1.2 });
    doc.text(String(area).slice(0, 18), 460, y + 15);
    y += rowH;
  });
  y += 10;
  if (y > 720) {
    doc.addPage();
    y = 60;
  }
  doc.setFont("helvetica", "bold");
  doc.text("Detailed challans follow on the next pages.", 40, y + 10);
}

export async function downloadCombinedChallanPDF(entries, { fileLabel = "combined", trip = {} } = {}) {
  if (!entries || entries.length === 0) return;
  const normalised = entries.map((e) =>
    e && e.order ? e : { order: e, kind: "delivery" }
  );
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  // Only render the vehicle manifest cover page when this trip has 2+ stops.
  const manifestNo = normalised.length > 1 ? _combinedChallanNumber() : null;
  if (manifestNo) {
    renderVehicleManifest(doc, normalised, manifestNo, trip);
  }
  // Pre-generate all QR codes in parallel — pass entry.kind so the URL
  // encodes whether this was scanned off a delivery challan or a return
  // memo, letting the Track page highlight the right date.
  const qrs = await Promise.all(
    normalised.map((e) => _orderQrDataUrl(e.order, e.kind))
  );
  normalised.forEach((entry, idx) => {
    const firstPage = !manifestNo && idx === 0;
    renderChallan(doc, entry.order, { firstPage, kind: entry.kind, trip, qrDataUrl: qrs[idx] });
  });

  const first = normalised[0];
  const singleLabel =
    !manifestNo && first
      ? `${(first.kind === "return" ? "Return-" : "")}${(first.order.customer_name || "challan").replace(/[^a-z0-9]+/gi, "_")}`
      : null;
  const name = manifestNo
    ? `Adams-Combined-${manifestNo}.pdf`
    : `Adams-${singleLabel || fileLabel}.pdf`;
  savePdf(doc, name);
  return manifestNo;
}

/**
 * Round 106.3 — All-vehicles combined challan PDF.
 *
 * Bundles delivery challans + return memos for EVERY vehicle of the day
 * into ONE PDF (with a manifest cover page per vehicle when the vehicle
 * has 2+ stops). Solves the browser-blocks-2nd-download issue that made
 * "Save & Generate Challans" only produce one vehicle's PDFs — this
 * generates a single file so there's no gesture/dedupe problem.
 *
 * @param {Array<{vehicle,driverName,dispatchTime,stops}>} trips
 * @param {{date?: string}} opts
 */
export async function downloadAllTripsChallanPDF(trips, { date = "" } = {}) {
  if (!trips || trips.length === 0) return 0;
  const valid = trips.filter((t) => t && Array.isArray(t.stops) && t.stops.length > 0);
  if (!valid.length) return 0;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  let firstPageWritten = false;

  for (const t of valid) {
    // Normalise each stop into the { order, kind } shape renderChallan needs.
    const normalised = t.stops.map((s) =>
      s && s.order ? { order: s.order, kind: s.kind || "delivery" } : { order: s, kind: "delivery" },
    );
    const tripMeta = {
      vehicle: t.vehicle || t.vehicle_no || "",
      driverName: t.driverName || t.driver_name || "",
      dispatchTime: t.dispatchTime || t.dispatch_time || "",
    };
    // Only render a manifest cover when this trip has 2+ stops
    // (matches downloadCombinedChallanPDF's behavior).
    const manifestNo = normalised.length > 1 ? _combinedChallanNumber() : null;
    if (manifestNo) {
      if (firstPageWritten) doc.addPage();
      renderVehicleManifest(doc, normalised, manifestNo, tripMeta);
      firstPageWritten = true;
    }
    // Pre-generate QR codes for this vehicle's stops in parallel.
    // eslint-disable-next-line no-await-in-loop
    const qrs = await Promise.all(
      normalised.map((e) => _orderQrDataUrl(e.order, e.kind)),
    );
    normalised.forEach((entry, idx) => {
      // `firstPage=true` means "use the current blank page"; `false`
      // means "addPage() first". The very first thing added to the doc
      // reuses the auto-created page 1; everything else adds a new page.
      const firstPage = !firstPageWritten;
      renderChallan(doc, entry.order, {
        firstPage,
        kind: entry.kind,
        trip: tripMeta,
        qrDataUrl: qrs[idx],
      });
      firstPageWritten = true;
    });
  }

  const stamp = (date || new Date().toISOString().slice(0, 10)).replace(/[^0-9-]/g, "");
  savePdf(doc, `Adams-Trip-Challans-${stamp}.pdf`);
  return valid.length;
}

export async function downloadBulkChallanPDF(orders, { fileLabel = "bulk", kind = "delivery" } = {}) {
  if (!orders || orders.length === 0) return;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const qrs = await Promise.all(orders.map((o) => _orderQrDataUrl(o, kind)));
  orders.forEach((o, i) => {
    const trip = _tripFromOrder(o, kind);
    renderChallan(doc, o, { firstPage: i === 0, kind, qrDataUrl: qrs[i], trip });
  });
  const prefix = kind === "return" ? "Return-Memos" : "Challans";
  savePdf(doc, `Adams-${prefix}-${fileLabel}.pdf`);
}

export function downloadFinanceReportPDF({
  date,
  receipts,
  expenses,
  totalReceipts,
  totalExpenses,
  netCash,
  categoryLabel,
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }

  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  const rightX = pageW - 40;
  let ry = 42;
  doc.text("Registered Office", rightX, ry, { align: "right" });
  ry += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const addrLines = doc.splitTextToSize(COMPANY.address, 280);
  doc.text(addrLines, rightX, ry, { align: "right" });
  ry += addrLines.length * 12 + 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`GSTIN: ${COMPANY.gst}`, rightX, ry, { align: "right" });
  ry += 12;
  doc.text(`SAC: ${COMPANY.sac}`, rightX, ry, { align: "right" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("DAILY CASH REPORT", pageW / 2, 136, { align: "center" });
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(formatDate(date), pageW / 2, 154, { align: "center" });

  // Summary box
  let y = 180;
  doc.setDrawColor(226, 232, 240);
  doc.rect(40, y, pageW - 80, 72);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text("Receipts", 60, y + 20);
  doc.text("Expenses", pageW / 2 - 40, y + 20);
  doc.text("Net Cash in Hand", pageW - 160, y + 20);
  doc.setFontSize(16);
  doc.setTextColor(17, 24, 39);
  doc.text(`Rs. ${fmt(totalReceipts)}`, 60, y + 48);
  doc.text(`Rs. ${fmt(totalExpenses)}`, pageW / 2 - 40, y + 48);
  doc.setTextColor(netCash >= 0 ? 22 : 220, netCash >= 0 ? 163 : 38, netCash >= 0 ? 74 : 38);
  doc.text(`Rs. ${fmt(netCash)}`, pageW - 160, y + 48);
  y += 96;

  // jsPDF's standard Helvetica/Times fonts do NOT include the Rupee glyph
  // (U+20B9). When that codepoint appears in any text run it corrupts the
  // entire run's kerning — characters bunch up in weird places. We sanitize
  // every user-provided string before drawing it. UI keeps the ₹ symbol;
  // only the PDF substitutes "Rs. ".
  const safe = (s) => String(s ?? "").replace(/\u20B9\s*/g, "Rs. ");

  // Receipts
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Cash Receipts", 40, y);
  y += 12;
  doc.setFillColor(248, 250, 252);
  doc.rect(40, y, pageW - 80, 22, "F");
  doc.setFontSize(9);
  doc.text("Client", 52, y + 15);
  doc.text("Order No.", 240, y + 15);
  doc.text("Notes", 340, y + 15);
  doc.text("Amount", pageW - 90, y + 15, { align: "right" });
  y += 22;

  // Column max widths for wrapping
  const RCPT_CLIENT_W = 180;
  const RCPT_ORDER_W = 90;
  const RCPT_NOTES_W = pageW - 90 - 340 - 5; // amount column starts ~ pageW-90
  const LINE_H = 12;

  receipts.forEach((r) => {
    // Defensive reset — if any earlier code path (totals, headers) left
    // bold/coloured state behind, the row would render in a different
    // font weight. Always start each row in the same baseline style.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(17, 24, 39);

    const clientLines = doc.splitTextToSize(safe(r.client_name || "—"), RCPT_CLIENT_W);
    const orderLines = doc.splitTextToSize(safe(r.order_number || "—"), RCPT_ORDER_W);
    const notesLines = doc.splitTextToSize(safe(r.notes || "—"), RCPT_NOTES_W);
    const lineCount = Math.max(clientLines.length, orderLines.length, notesLines.length, 1);
    const rowH = lineCount * LINE_H + 6;

    if (y + rowH > 780) {
      doc.addPage();
      y = 60;
    }
    doc.text(clientLines, 52, y + 12);
    doc.text(orderLines, 240, y + 12);
    doc.text(notesLines, 340, y + 12);
    doc.text(`Rs. ${fmt(r.amount)}`, pageW - 50, y + 12, { align: "right" });
    y += rowH;
  });
  if (receipts.length === 0) {
    doc.setTextColor(107, 114, 128);
    doc.text("No receipts.", 52, y + 14);
    doc.setTextColor(17, 24, 39);
    y += 20;
  } else {
    // Total row for Cash Receipts
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(0.8);
    doc.line(40, y + 2, pageW - 40, y + 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(`Total · ${receipts.length} ${receipts.length === 1 ? "entry" : "entries"}`, 52, y + 18);
    doc.setTextColor(22, 163, 74);
    doc.text(`Rs. ${fmt(totalReceipts)}`, pageW - 50, y + 18, { align: "right" });
    doc.setTextColor(17, 24, 39);
    doc.setFont("helvetica", "normal");
    y += 26;
  }
  y += 14;

  // Expenses
  if (y > 700) {
    doc.addPage();
    y = 60;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Expenses", 40, y);
  y += 12;
  doc.setFillColor(248, 250, 252);
  doc.rect(40, y, pageW - 80, 22, "F");
  doc.setFontSize(9);
  doc.text("Category", 52, y + 15);
  doc.text("Description", 220, y + 15);
  doc.text("Amount", pageW - 90, y + 15, { align: "right" });
  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(17, 24, 39);

  // Expenses column widths
  const EXP_CAT_W = 160;
  const EXP_DESC_W = pageW - 90 - 220 - 5;

  expenses.forEach((e) => {
    if (y > 760) {
      doc.addPage();
      y = 60;
    }
    // Reset font/colour every row defensively — guarantees the Category
    // column reads the same regardless of whether earlier code paths
    // (e.g. Total row above) left bold/coloured state behind. Applies
    // uniformly to all categories, including custom ones added later.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(17, 24, 39);

    const catLines = doc.splitTextToSize(
      safe((categoryLabel && categoryLabel[e.category]) || e.category),
      EXP_CAT_W
    );
    const descLines = doc.splitTextToSize(safe(e.description || "—"), EXP_DESC_W);
    const lineCount = Math.max(catLines.length, descLines.length, 1);
    const rowH = lineCount * LINE_H + 6;

    if (y + rowH > 780) {
      doc.addPage();
      y = 60;
    }
    doc.text(catLines, 52, y + 12);
    doc.text(descLines, 220, y + 12);
    doc.text(`Rs. ${fmt(e.amount)}`, pageW - 50, y + 12, { align: "right" });
    y += rowH;
  });
  if (expenses.length === 0) {
    doc.setTextColor(107, 114, 128);
    doc.text("No expenses.", 52, y + 14);
    doc.setTextColor(17, 24, 39);
  } else {
    // Total row for Expenses
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(0.8);
    doc.line(40, y + 2, pageW - 40, y + 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(`Total · ${expenses.length} ${expenses.length === 1 ? "entry" : "entries"}`, 52, y + 18);
    doc.setTextColor(220, 38, 38);
    doc.text(`Rs. ${fmt(totalExpenses)}`, pageW - 50, y + 18, { align: "right" });
    doc.setTextColor(17, 24, 39);
    doc.setFont("helvetica", "normal");
  }

  savePdf(doc, `Adams-Cash-Report-${date}.pdf`);
}

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN");
}

// ──────────────────────────────────────────────────────────────────
// Outstanding payments report — admin-printable list of every order
// with a non-zero balance. Columns: Order No., Client, Phone, Event
// Date, Total, Balance. Sorted oldest event-date first.
// ──────────────────────────────────────────────────────────────────
export function downloadOutstandingPDF(rows) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }

  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("OUTSTANDING PAYMENTS", pageW / 2, 70, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  const today = new Date().toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
  doc.text(`Generated ${today}  ·  ${rows.length} orders`, pageW / 2, 86, { align: "center" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  let y = 134;
  doc.setFillColor(248, 250, 252);
  doc.rect(40, y, pageW - 80, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(75, 85, 99);
  // Six columns. Allotment (A4 width ≈ 595pt, gutter 40 each side = 515pt usable):
  //   Order # 50  | Client 130 | Phone 75 | Event Date 75 | Total 70 (right) | Balance 70 (right)
  // Right-aligned anchor for Total puts its END at totalRX so the value
  // never collides with Event Date on the left or Balance on the right.
  const totalRX = pageW - 130; // Total value ends here
  const balRX = pageW - 40;    // Balance value ends here (page edge)
  doc.text("Order #", 52, y + 15);
  doc.text("Client", 105, y + 15);
  doc.text("Phone", 240, y + 15);
  doc.text("Event Date", 318, y + 15);
  doc.text("Total", totalRX, y + 15, { align: "right" });
  doc.text("Balance", balRX, y + 15, { align: "right" });
  y += 22;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(17, 24, 39);

  let total = 0;
  rows.forEach((r) => {
    if (y > pageH - 80) {
      doc.addPage();
      y = 60;
    }
    doc.text(String(r.order_number || "—").slice(0, 12), 52, y + 14);
    doc.text(String(r.customer_name || "—").slice(0, 22), 105, y + 14);
    doc.text(String(r.customer_phone || "—").slice(0, 12), 240, y + 14);
    doc.text(r.event_date ? formatDate(r.event_date) : "—", 318, y + 14);
    doc.text(`Rs. ${fmt(r.total_amount)}`, totalRX, y + 14, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.setTextColor(220, 38, 38);
    doc.text(`Rs. ${fmt(r.balance_due)}`, balRX, y + 14, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(17, 24, 39);
    total += Number(r.balance_due || 0);
    y += 18;
  });

  if (rows.length === 0) {
    doc.setTextColor(107, 114, 128);
    doc.text("No outstanding payments.", pageW / 2, y + 30, { align: "center" });
  } else {
    if (y > pageH - 80) { doc.addPage(); y = 60; }
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(1.2);
    doc.line(pageW - 240, y + 6, balRX, y + 6);
    y += 22;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Total Outstanding", pageW - 240, y);
    doc.setFontSize(13);
    doc.setTextColor(220, 38, 38);
    doc.text(`Rs. ${fmt(total)}`, balRX, y, { align: "right" });
  }

  savePdf(doc, `Adams-Outstanding-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ==============================================================
// Overdue Payments — event already past + balance due (Round 105)
// Adds an "Overdue Days" column and colours by staleness. Used
// from the red "Overdue payments" banner on the Dashboard/Orders.
// ==============================================================
export function downloadOverduePaymentsPDF(rows, meta = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Header
  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("OVERDUE PAYMENTS", pageW / 2, 70, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  const today = new Date().toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
  const minDays = Number(meta.min_days || 7);
  doc.text(
    `Generated ${today}  ·  ${rows.length} orders overdue by ${minDays}+ days`,
    pageW / 2, 86, { align: "center" }
  );

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  // KPI strip
  const total = rows.reduce((s, r) => s + Number(r.balance_due || 0), 0);
  const worst = rows.reduce((m, r) => Math.max(m, Number(r.overdue_days || 0)), 0);
  const avg = rows.length ? Math.round(
    rows.reduce((s, r) => s + Number(r.overdue_days || 0), 0) / rows.length
  ) : 0;

  let y = 128;
  const kpis = [
    ["ORDERS OVERDUE", String(rows.length), [17, 24, 39]],
    ["TOTAL CHASING", `Rs. ${fmt(total)}`, [185, 28, 28]],
    ["WORST OVERDUE", `${worst} d`, [180, 83, 9]],
    ["AVG OVERDUE", `${avg} d`, [107, 114, 128]],
  ];
  const cellW = (pageW - 80) / 4;
  kpis.forEach(([label, value, tone], i) => {
    const x = 40 + i * cellW;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.8);
    doc.roundedRect(x + 4, y, cellW - 8, 50, 4, 4);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(107, 114, 128);
    doc.text(label, x + 12, y + 16);
    doc.setFontSize(13);
    doc.setTextColor(...tone);
    doc.text(value, x + 12, y + 38);
  });
  y += 66;

  // Table header
  doc.setFillColor(254, 226, 226); // red-100
  doc.rect(40, y, pageW - 80, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(127, 29, 29); // red-900
  // 6 columns on A4 portrait (~515pt usable)
  //   Order # / Client / Phone / Event Date / Overdue / Balance
  const balRX = pageW - 40;
  const overRX = pageW - 110;
  doc.text("ORDER #", 52, y + 15);
  doc.text("CLIENT", 108, y + 15);
  doc.text("PHONE", 240, y + 15);
  doc.text("EVENT DATE", 320, y + 15);
  doc.text("OVERDUE", overRX, y + 15, { align: "right" });
  doc.text("BALANCE", balRX, y + 15, { align: "right" });
  y += 22;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(17, 24, 39);

  const sorted = (rows || []).slice().sort(
    (a, b) => Number(b.overdue_days || 0) - Number(a.overdue_days || 0)
  );
  sorted.forEach((r, i) => {
    if (y > pageH - 90) {
      doc.addPage();
      y = 60;
    }
    // Alternating row shade
    if (i % 2 === 1) {
      doc.setFillColor(250, 250, 250);
      doc.rect(40, y, pageW - 80, 20, "F");
    }
    const d = Number(r.overdue_days || 0);
    doc.setTextColor(17, 24, 39);
    doc.text(String(r.order_number || "—").slice(0, 12), 52, y + 14);
    doc.text(String(r.customer_name || "—").slice(0, 22), 108, y + 14);
    doc.text(String(r.customer_phone || "—").slice(0, 12), 240, y + 14);
    doc.text(r.event_date ? formatDate(r.event_date) : "—", 320, y + 14);
    // Overdue days — red bold if > 30d, amber if > 14d
    doc.setFont("helvetica", "bold");
    if (d > 30) doc.setTextColor(185, 28, 28);
    else if (d > 14) doc.setTextColor(180, 83, 9);
    else doc.setTextColor(120, 53, 15);
    doc.text(`${d}d`, overRX, y + 14, { align: "right" });
    // Balance
    doc.setTextColor(185, 28, 28);
    doc.text(`Rs. ${fmt(r.balance_due)}`, balRX, y + 14, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(17, 24, 39);
    y += 20;
  });

  if (sorted.length === 0) {
    doc.setTextColor(4, 120, 87);
    doc.text("No overdue payments. Great job!", pageW / 2, y + 30, { align: "center" });
  } else {
    if (y > pageH - 90) { doc.addPage(); y = 60; }
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(1.2);
    doc.line(pageW - 240, y + 6, balRX, y + 6);
    y += 22;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.text("Total Overdue", pageW - 240, y);
    doc.setFontSize(13);
    doc.setTextColor(185, 28, 28);
    doc.text(`Rs. ${fmt(total)}`, balRX, y, { align: "right" });
  }

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(
    `Adams Bangalore  ·  ${COMPANY.web}  ·  Chase these customers via WhatsApp/Call`,
    pageW / 2, pageH - 30, { align: "center" }
  );

  savePdf(doc, `Adams-Overdue-Payments-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ──────────────────────────────────────────────────────────────────
// Day Summary — single-page brief for a particular calendar date.
// Includes: deliveries scheduled, returns scheduled, totals + balance.
// Triggered from the Calendar Analytics dialog.
// ──────────────────────────────────────────────────────────────────
export function downloadDaySummaryPDF({ date, deliveries = [], returns = [] }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("DAY SUMMARY", pageW / 2, 70, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(107, 114, 128);
  doc.text(formatDate(date), pageW / 2, 88, { align: "center" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  // KPI strip
  const totalRev = [...deliveries, ...returns].reduce(
    (s, o) => s + Number(o.total_amount || 0), 0
  );
  const totalBal = [...deliveries, ...returns].reduce(
    (s, o) => s + Number(o.balance_due || 0), 0
  );
  let y = 134;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(75, 85, 99);
  const cellW = (pageW - 80) / 4;
  const kpis = [
    ["DELIVERIES", String(deliveries.length)],
    ["RETURNS", String(returns.length)],
    ["TOTAL VALUE", `Rs. ${fmt(totalRev)}`],
    ["BALANCE DUE", `Rs. ${fmt(totalBal)}`],
  ];
  kpis.forEach(([label, value], i) => {
    const x = 40 + i * cellW;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.8);
    doc.roundedRect(x + 4, y, cellW - 8, 50, 4, 4);
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text(label, x + 12, y + 16);
    doc.setFontSize(14);
    doc.setTextColor(17, 24, 39);
    doc.text(value, x + 12, y + 38);
  });
  y += 70;

  const renderSection = (title, list, tone) => {
    if (y > pageH - 100) { doc.addPage(); y = 60; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...tone);
    doc.text(`${title} (${list.length})`, 40, y);
    y += 14;
    if (list.length === 0) {
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      doc.setFont("helvetica", "italic");
      doc.text("None scheduled.", 40, y + 8);
      y += 22;
      return;
    }
    doc.setFillColor(248, 250, 252);
    doc.rect(40, y, pageW - 80, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(75, 85, 99);
    doc.text("Order #", 52, y + 15);
    doc.text("Customer", 130, y + 15);
    doc.text("Phone", 290, y + 15);
    doc.text("Items", 380, y + 15);
    doc.text("Total", pageW - 130, y + 15, { align: "right" });
    doc.text("Balance", pageW - 50, y + 15, { align: "right" });
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(17, 24, 39);
    list.forEach((o) => {
      if (y > pageH - 80) { doc.addPage(); y = 60; }
      doc.text(String(o.order_number || "—").slice(0, 12), 52, y + 14);
      doc.text(String(o.customer_name || "—").slice(0, 28), 130, y + 14);
      doc.text(String(o.customer_phone || "—").slice(0, 12), 290, y + 14);
      doc.text(String((o.items || []).length), 380, y + 14);
      doc.text(`Rs. ${fmt(o.total_amount)}`, pageW - 130, y + 14, { align: "right" });
      const bal = Number(o.balance_due || 0);
      if (bal > 0) {
        doc.setTextColor(220, 38, 38);
        doc.setFont("helvetica", "bold");
      }
      doc.text(`Rs. ${fmt(bal)}`, pageW - 50, y + 14, { align: "right" });
      doc.setTextColor(17, 24, 39);
      doc.setFont("helvetica", "normal");
      y += 18;
    });
    y += 12;
  };

  renderSection("DELIVERIES", deliveries, [220, 38, 38]);
  renderSection("RETURNS", returns, [126, 34, 206]);

  savePdf(doc, `Adams-DaySummary-${date}.pdf`);
}

// ──────────────────────────────────────────────────────────────────
// Trip Planner Summary — one section per vehicle, listing the route
// in order with the kind (DELIVER / PICKUP) tag for each stop.
// Used for the printable handover sheet given to the dispatch supervisor.
// ──────────────────────────────────────────────────────────────────
// Round 102 — Vehicle-specific fuel rate (km per litre). Kept in
// sync with the constants in /app/frontend/src/pages/Logistics.jsx.
const PDF_VEHICLE_KMPL = { "7471": 12, "408": 6, "409": 6, "7456": 6 };
const PDF_DEFAULT_KMPL = 6;
function _pdfKmplFor(vehicleNo) {
  return PDF_VEHICLE_KMPL[String(vehicleNo || "").trim()] || PDF_DEFAULT_KMPL;
}
function _pdfLitresFor(vehicleNo, km) {
  if (km == null || isNaN(km)) return null;
  return Math.round((Number(km) / _pdfKmplFor(vehicleNo)) * 10) / 10;
}

// Round 102.1 — Format "15:00" as "3 PM" for the PDF too.
function _pdfFormatTime(t) {
  if (!t) return "";
  const s = String(t).trim();
  if (!s) return "";
  if (/[ap]m$/i.test(s)) return s.toUpperCase();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return s;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return mm === "00" ? `${h} ${suffix}` : `${h}:${mm} ${suffix}`;
}

export function downloadTripSummaryPDF(trips, { date, distances = {}, totals = null, kpis = null } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }

  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("TRIP PLANNER SUMMARY", pageW / 2, 70, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  const totalStops = trips.reduce((s, t) => s + (t.stops?.length || 0), 0);
  doc.text(
    `${date ? formatDate(date) : "All trips"}  ·  ${trips.length} trips  ·  ${totalStops} stops`,
    pageW / 2, 86, { align: "center" }
  );

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  let y = 130;

  // Round 102 — Quick-glance KPI band at the top of the PDF so ops
  // grok the day in <5 seconds. 7 cells across the page width.
  if (kpis) {
    const kpiList = [
      { label: "VEHICLES", value: String(kpis.vehicles ?? "—") },
      { label: "TRIPS", value: String(kpis.trips ?? "—") },
      { label: "DELIVERIES", value: String(kpis.deliveries ?? "—"), colour: [220, 38, 38] },
      { label: "PICKUPS", value: String(kpis.pickups ?? "—"), colour: [147, 51, 234] },
      { label: "STOPS", value: String(kpis.stops ?? "—") },
      { label: "TOTAL KM", value: totals?.km != null ? `${totals.km}` : "—", colour: [5, 150, 105] },
      { label: "FUEL (L)", value: totals?.litres != null ? `${totals.litres}` : "—", colour: [217, 119, 6] },
    ];
    const kpiCount = kpiList.length;
    const gap = 6;
    const kpiW = (pageW - 80 - (kpiCount - 1) * gap) / kpiCount;
    const kpiH = 40;
    kpiList.forEach((k, i) => {
      const x = 40 + i * (kpiW + gap);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.rect(x, y, kpiW, kpiH, "FD");
      const [r, g, b] = k.colour || [148, 163, 184];
      doc.setFillColor(r, g, b);
      doc.rect(x, y, 3, kpiH, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(100, 116, 139);
      doc.text(k.label, x + 8, y + 12);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(15, 23, 42);
      doc.text(k.value, x + 8, y + 30);
    });
    y += kpiH + 12;
  }

  // Round 100.1 — Fleet Fuel & Distance summary at the top of the PDF.
  // Grouped by vehicle: a vehicle running multiple trips shows a bold
  // TOTAL row + indented per-trip breakdown. Header shows only the
  // grand km on the right and the grand fuel in red below it — no
  // subtitle. `~` prefix (not `≈`) because helvetica in jsPDF lacks
  // that glyph and it was rendering as a garbled `"H` in production.
  const distEntries = trips.map((t) => {
    const key = `${t.vehicle_no || ""}|${t.driver_name || ""}|${t.dispatch_time || ""}`;
    return { trip: t, key, dist: distances[key] || {} };
  });
  const hasAnyDistance = distEntries.some((e) => e.dist && e.dist.km != null);
  if (hasAnyDistance) {
    // Group entries by vehicle (preserving earliest-dispatch order)
    const byVeh = [];
    const vIndex = new Map();
    distEntries.forEach((e) => {
      const vNo = e.trip.vehicle_no || "Unassigned";
      let bucket = vIndex.get(vNo);
      if (!bucket) {
        bucket = {
          vehicle_no: vNo,
          entries: [],
          totalKm: 0,
          totalLitres: 0,
          totalStops: 0,
          firstTime: e.trip.dispatch_time || "",
        };
        vIndex.set(vNo, bucket);
        byVeh.push(bucket);
      }
      bucket.entries.push(e);
      bucket.totalKm += e.dist?.km || 0;
      bucket.totalLitres += e.dist?.litres || 0;
      bucket.totalStops += e.trip.stops?.length || 0;
    });

    // Compact grand totals header band — fleet info is now secondary
    // to the trips/stops content below, so we shrink it.
    doc.setFillColor(15, 23, 42);
    doc.rect(40, y, pageW - 80, 32, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("FLEET DISTANCE & FUEL — PLANNED", 52, y + 20);
    const totalKm = totals?.km != null ? totals.km : distEntries.reduce((s, e) => s + (e.dist?.km || 0), 0);
    // Round 102 — Sum per-trip litres using vehicle-specific rate.
    const totalLit =
      totals?.litres != null
        ? totals.litres
        : Math.round(
            distEntries.reduce(
              (s, e) => s + (e.dist?.litres != null ? e.dist.litres : (_pdfLitresFor(e.trip.vehicle_no, e.dist?.km) || 0)),
              0,
            ) * 10,
          ) / 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(`${Math.round(totalKm * 10) / 10} km`, pageW - 120, y + 20, { align: "right" });
    doc.setTextColor(252, 165, 165);
    doc.text(`${totalLit} L fuel`, pageW - 52, y + 20, { align: "right" });
    y += 34;

    // Per-vehicle table header
    doc.setFillColor(248, 250, 252);
    doc.rect(40, y, pageW - 80, 20, "F");
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.rect(40, y, pageW - 80, 20);
    doc.setTextColor(75, 85, 99);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("VEHICLE", 52, y + 13);
    doc.text("DRIVER / TRIP", 180, y + 13);
    doc.text("STOPS", pageW - 200, y + 13, { align: "right" });
    doc.text("KM", pageW - 130, y + 13, { align: "right" });
    doc.text("LITRES", pageW - 52, y + 13, { align: "right" });
    y += 20;

    byVeh.forEach((bucket) => {
      const multi = bucket.entries.length > 1;
      const rowH = 22;
      if (y > pageH - 80) { doc.addPage(); y = 60; }
      // Main (vehicle total) row
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(40, y + rowH, pageW - 40, y + rowH);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 39);
      doc.text(String(bucket.vehicle_no).slice(0, 18), 52, y + 14);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(75, 85, 99);
      const driver = bucket.entries[0]?.trip?.driver_name || "";
      const label = multi
        ? `${driver}  ·  ${bucket.entries.length} trips today`
        : `${driver}${bucket.entries[0]?.trip?.dispatch_time ? "  ·  " + _pdfFormatTime(bucket.entries[0].trip.dispatch_time) : ""}`;
      doc.text(label.slice(0, 40), 180, y + 14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(17, 24, 39);
      doc.text(String(bucket.totalStops), pageW - 200, y + 14, { align: "right" });
      doc.text(`${Math.round(bucket.totalKm * 10) / 10} km`, pageW - 130, y + 14, { align: "right" });
      doc.setTextColor(185, 28, 28);
      doc.text(
        `${Math.round(bucket.totalLitres * 10) / 10} L`,
        pageW - 52, y + 14, { align: "right" },
      );
      doc.setTextColor(17, 24, 39);
      y += rowH;

      // Indented sub-rows only when the vehicle runs more than one trip
      if (multi) {
        bucket.entries.forEach((e, si) => {
          if (y > pageH - 80) { doc.addPage(); y = 60; }
          doc.setDrawColor(241, 245, 249);
          doc.line(40, y + rowH, pageW - 40, y + rowH);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(107, 114, 128);
          // Round 100.2 — Use plain-ascii indent. The `↳` glyph is
          // absent from jsPDF's default helvetica and was rendering
          // as garbled `l³` in production.
          doc.text(`Trip ${si + 1}`, 68, y + 14);
          doc.text(
            (e.trip.dispatch_time ? _pdfFormatTime(e.trip.dispatch_time) : "—").slice(0, 20),
            180, y + 14,
          );
          doc.text(String(e.trip.stops?.length || 0), pageW - 200, y + 14, { align: "right" });
          doc.text(
            e.dist?.km != null ? `${e.dist.km} km` : "—",
            pageW - 130, y + 14, { align: "right" },
          );
          doc.setTextColor(185, 28, 28);
          doc.text(
            e.dist?.litres != null ? `${e.dist.litres} L` : "—",
            pageW - 52, y + 14, { align: "right" },
          );
          doc.setTextColor(107, 114, 128);
          y += rowH;
        });
      }
    });
    y += 12;
  }

  // ------------------------------------------------------------------
  // Trip cards below — grouped by vehicle. A vehicle with 2 trips gets
  // ONE header band showing the total km / litres / stops for the day,
  // then each sub-trip is a smaller sub-header + stop list separated by
  // a "BACK TO GODOWN · NEXT TRIP" divider strip.
  // ------------------------------------------------------------------
  const vehicleGroups = [];
  const vgIndex = new Map();
  trips.forEach((t) => {
    const vNo = t.vehicle_no || "Unassigned";
    let bucket = vgIndex.get(vNo);
    if (!bucket) {
      bucket = { vehicle_no: vNo, trips: [] };
      vgIndex.set(vNo, bucket);
      vehicleGroups.push(bucket);
    }
    bucket.trips.push(t);
  });
  // Sort each vehicle's trips by dispatch time
  vehicleGroups.forEach((g) =>
    g.trips.sort((a, b) => (a.dispatch_time || "").localeCompare(b.dispatch_time || "")),
  );

  vehicleGroups.forEach((veh) => {
    if (y > pageH - 200) { doc.addPage(); y = 60; }

    // Compute vehicle-level totals
    let vTotalKm = 0, vTotalLit = 0, vTotalStops = 0;
    veh.trips.forEach((t) => {
      const k = `${t.vehicle_no || ""}|${t.driver_name || ""}|${t.dispatch_time || ""}`;
      const d = distances[k];
      if (d?.km != null) vTotalKm += d.km;
      if (d?.litres != null) vTotalLit += d.litres;
      vTotalStops += t.stops?.length || 0;
    });

    // ---- VEHICLE HEADER (the highlight) ----
    // Big vehicle number on the LEFT. Compact totals meta line to
    // the RIGHT, single row so nothing overlaps the red rule.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(15, 23, 42);
    const vehLabel = String(veh.vehicle_no || "Unassigned");
    doc.text(vehLabel, 40, y + 18);

    // Right-side meta: driver · stops · km · L on one line (each with dot separator)
    const driverName = veh.trips[0]?.driver_name || "";
    const metaParts = [];
    if (driverName) metaParts.push(driverName);
    metaParts.push(`${vTotalStops} ${vTotalStops === 1 ? "stop" : "stops"}`);
    if (vTotalKm > 0) metaParts.push(`${Math.round(vTotalKm * 10) / 10} km`);
    if (vTotalLit > 0) metaParts.push(`${Math.round(vTotalLit * 10) / 10} L`);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    doc.text(metaParts.join("  ·  "), pageW - 40, y + 16, { align: "right" });

    // Red rule
    doc.setDrawColor(220, 38, 38);
    doc.setLineWidth(1.5);
    doc.line(40, y + 26, pageW - 40, y + 26);
    y += 40;

    veh.trips.forEach((t, tIdx) => {
      if (y > pageH - 160) { doc.addPage(); y = 60; }
      if (tIdx > 0) y += 10; // gap between trip boxes

      const kindsSet =
        t.kinds && typeof t.kinds.has === "function"
          ? t.kinds
          : new Set((t.stops || []).map((s) => s.kind || (s.order?.trip_kind) || "delivery"));
      const subLabel =
        kindsSet.has("delivery") && kindsSet.has("return")
          ? "DELIVER + PICKUP"
          : kindsSet.has("return")
            ? "PICKUP"
            : "DELIVER";
      const tripKey = `${t.vehicle_no || ""}|${t.driver_name || ""}|${t.dispatch_time || ""}`;
      const td = distances[tripKey];
      const stopCount = t.stops?.length || 0;

      // Round 103 — Each trip renders inside its own bordered box so
      // ops can visually scan trip-by-trip. Header stripe (slate-50)
      // holds Trip N · driver · dispatch · N stops; km/L sits on the
      // right in red. Stops list follows inside the same box.
      const boxStartY = y;
      const boxX = 40;
      const boxW = pageW - 80;
      const HEADER_H = 34;

      // Placeholder for the box outline (drawn after the stops so
      // height is known).
      // Header stripe fill
      doc.setFillColor(248, 250, 252);
      doc.rect(boxX, y, boxW, HEADER_H, "F");
      // Header content
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(17, 24, 39);
      const parts = [`Trip ${tIdx + 1}`];
      if (t.driver_name) parts.push(t.driver_name);
      if (t.dispatch_time) parts.push(_pdfFormatTime(t.dispatch_time));
      parts.push(`${stopCount} ${stopCount === 1 ? "stop" : "stops"}`);
      doc.text(parts.join("  ·  "), boxX + 10, y + 14);
      // Kind label right (line 1)
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(107, 114, 128);
      doc.text(subLabel, boxX + boxW - 10, y + 14, { align: "right" });
      // km / L right (line 2, red)
      if (td?.km != null) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(185, 28, 28);
        doc.text(
          `${td.km} km  ·  ${td.litres != null ? td.litres + " L" : "—"}`,
          boxX + boxW - 10, y + 28, { align: "right" },
        );
      }
      // Divider line under the header stripe
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(boxX, y + HEADER_H, boxX + boxW, y + HEADER_H);
      y += HEADER_H + 6;

      // ---- STOPS (inside the box, indented) ----
      const COL_NUM = 52;
      const COL_TAG = 72;
      const COL_NAME = 140;
      const ADDR_MAX_W = pageW - COL_NAME - 100;

      t.stops.forEach((s, i) => {
        if (y > pageH - 90) {
          // finish current box and start a fresh one on next page
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.5);
          doc.rect(boxX, boxStartY, boxW, y - boxStartY);
          doc.addPage();
          y = 60;
        }
        const o = s.order || s;
        const isReturn = s.kind
          ? s.kind === "return"
          : o.trip_kind === "return";
        const tag = isReturn ? "[PICKUP]" : "[DELIVER]";
        // Round 105.2 — 2-loads sub-tag rendered on a dedicated line
        // BELOW the DELIVER/PICKUP badge (smaller, amber). Prevents the
        // long name column from overlapping the tag on wide addresses.
        const loadTag =
          s.loadIndex === 2
            ? "LOAD 2 / 2"
            : !isReturn && o.two_loads
              ? "LOAD 1 / 2"
              : "";

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text(`${i + 1}.`, COL_NUM, y + 14);
        if (isReturn) doc.setTextColor(126, 34, 206);
        else doc.setTextColor(220, 38, 38);
        doc.text(tag, COL_TAG, y + 14);
        if (loadTag) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(180, 83, 9); // amber-700
          doc.text(loadTag, COL_TAG, y + 24);
          doc.setFontSize(9);
        }
        doc.setTextColor(17, 24, 39);
        doc.setFontSize(10);
        const nameSafe = String(o.customer_name || "—");
        const nameMax = doc.splitTextToSize(nameSafe, pageW - COL_NAME - 100);
        doc.text(nameMax[0], COL_NAME, y + 14);
        const phone = String(o.customer_phone || "").trim();
        if (phone) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(75, 85, 99);
          doc.text(phone, pageW - 50, y + 14, { align: "right" });
        }
        // If the load tag is rendered, push the address block down so
        // it doesn't collide with the tag at y+24.
        const addrYBase = loadTag ? 36 : 26;
        const addrRowExtra = loadTag ? 10 : 0;
        const addr = String(o.address || "").trim();
        if (addr) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8.5);
          doc.setTextColor(100, 116, 139);
          const wrapped = doc.splitTextToSize(addr, ADDR_MAX_W);
          const capped = wrapped.slice(0, 2);
          capped.forEach((ln, li) => {
            doc.text(ln, COL_NAME, y + addrYBase + li * 10);
          });
          y += addrYBase + (capped.length - 1) * 10;
        } else {
          y += 16 + addrRowExtra;
        }
        y += 10;
      });

      // Small bottom padding inside the box
      y += 4;
      // Draw the box outline now that we know the height
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.rect(boxX, boxStartY, boxW, y - boxStartY);
    });

    y += 12;
  });

  if (trips.length === 0) {
    doc.setTextColor(107, 114, 128);
    doc.text("No trips planned for this date.", pageW / 2, y + 30, { align: "center" });
  }

  savePdf(doc, `Adams-Trip-Summary-${date || new Date().toISOString().slice(0, 10)}.pdf`);
}


// Designed to be handed to godown staff for physical loading.
// ──────────────────────────────────────────────────────────────────
export function downloadItemsToLoadPDF(trips, { date, label = "Items to Load" } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }

  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(label.toUpperCase(), pageW / 2, 70, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text(date ? formatDate(date) : "All vehicles", pageW / 2, 86, { align: "center" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  let y = 134;

  trips.forEach((trip, idx) => {
    if (y > pageH - 120) { doc.addPage(); y = 60; }

    // Vehicle header band
    doc.setFillColor(15, 23, 42);
    doc.rect(40, y, pageW - 80, 32, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`VEHICLE ${idx + 1}: ${trip.vehicle_no || "—"}`, 52, y + 14);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${trip.driver_name || "—"}${trip.dispatch_time ? "  ·  " + _pdfFormatTime(trip.dispatch_time) : ""}  ·  ${trip.stop_count || 0} stops`,
      52, y + 26,
    );
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(`${trip.items?.length || 0} item types`, pageW - 50, y + 20, { align: "right" });
    y += 36;

    // Items table header
    doc.setFillColor(248, 250, 252);
    doc.rect(40, y, pageW - 80, 22, "F");
    doc.setTextColor(75, 85, 99);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("S.No.", 52, y + 15);
    doc.text("Item", 100, y + 15);
    doc.text("Quantity", pageW - 50, y + 15, { align: "right" });
    y += 22;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(17, 24, 39);
    (trip.items || []).forEach((it, i) => {
      if (y > pageH - 60) { doc.addPage(); y = 60; }
      doc.text(String(i + 1), 52, y + 14);
      doc.text(String(it.name || "—").slice(0, 60), 100, y + 14);
      doc.setFont("helvetica", "bold");
      doc.text(String(it.quantity || 0), pageW - 50, y + 14, { align: "right" });
      doc.setFont("helvetica", "normal");
      y += 18;
    });

    if (!trip.items || trip.items.length === 0) {
      doc.setTextColor(107, 114, 128);
      doc.text("No items.", 100, y + 14);
      doc.setTextColor(17, 24, 39);
      y += 18;
    }

    // Sign-off line
    y += 8;
    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.6);
    doc.line(40, y + 14, 200, y + 14);
    doc.line(pageW - 200, y + 14, pageW - 40, y + 14);
    doc.setFontSize(7.5);
    doc.setTextColor(75, 85, 99);
    doc.text("Loaded by (godown)", 40, y + 24);
    doc.text("Driver signature", pageW - 200, y + 24);
    y += 36;
  });

  savePdf(doc, 
    `Adams-Items-to-Load-${(date || new Date().toISOString().slice(0, 10))}.pdf`
  );
}

export function downloadCustomerLedgerPDF(ledger) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const { customer, orders, receipts, totals } = ledger;

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }

  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  const rightX = pageW - 40;
  let ry = 42;
  doc.text("Registered Office", rightX, ry, { align: "right" });
  ry += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const addr = doc.splitTextToSize(COMPANY.address, 280);
  doc.text(addr, rightX, ry, { align: "right" });
  ry += addr.length * 12 + 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`GSTIN: ${COMPANY.gst}`, rightX, ry, { align: "right" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("CUSTOMER LEDGER", pageW / 2, 136, { align: "center" });

  doc.setFontSize(11);
  doc.text(customer.name || customer.phone, 40, 168);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(`Phone: ${customer.phone || "—"}`, 40, 184);
  if (customer.email) doc.text(`Email: ${customer.email}`, 40, 198);
  doc.text(
    `Generated: ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`,
    pageW - 40,
    184,
    { align: "right" }
  );

  // Summary box
  let y = 220;
  doc.setDrawColor(226, 232, 240);
  doc.rect(40, y, pageW - 80, 72);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text("Orders", 60, y + 20);
  doc.text("Billed", 160, y + 20);
  doc.text("Paid", 280, y + 20);
  doc.text("Balance Due", pageW - 160, y + 20);
  doc.setFontSize(15);
  doc.setTextColor(17, 24, 39);
  doc.text(String(totals.order_count || 0), 60, y + 48);
  doc.text(`Rs. ${fmt(totals.billed)}`, 160, y + 48);
  doc.setTextColor(16, 122, 87);
  doc.text(`Rs. ${fmt(totals.advance_on_orders)}`, 280, y + 48);
  doc.setTextColor(totals.balance_due > 0 ? 220 : 16, totals.balance_due > 0 ? 38 : 122, totals.balance_due > 0 ? 38 : 87);
  doc.text(`Rs. ${fmt(totals.balance_due)}`, pageW - 160, y + 48);
  y += 96;

  // Orders table
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Order History", 40, y);
  y += 12;
  doc.setFillColor(248, 250, 252);
  doc.rect(40, y, pageW - 80, 22, "F");
  doc.setFontSize(9);
  doc.text("Order No.", 52, y + 15);
  doc.text("Delivery", 140, y + 15);
  doc.text("Event", 220, y + 15);
  doc.text("Items", 300, y + 15);
  doc.text("Billed", pageW - 180, y + 15, { align: "right" });
  doc.text("Paid", pageW - 120, y + 15, { align: "right" });
  doc.text("Balance", pageW - 50, y + 15, { align: "right" });
  y += 22;
  doc.setFont("helvetica", "normal");
  orders.forEach((o) => {
    if (y > 760) {
      doc.addPage();
      y = 60;
    }
    doc.text(String(o.order_number || "—"), 52, y + 14);
    doc.text(formatDate(o.delivery_date).replace(" ", "-"), 140, y + 14);
    doc.text(o.event_date ? formatDate(o.event_date).replace(" ", "-") : "—", 220, y + 14);
    doc.text(String(o.items?.length || 0), 300, y + 14);
    doc.text(`Rs. ${fmt(o.total_amount)}`, pageW - 180, y + 14, { align: "right" });
    doc.text(`Rs. ${fmt(o.advance_amount)}`, pageW - 120, y + 14, { align: "right" });
    const bal = o.balance_due || 0;
    if (bal > 0) doc.setTextColor(220, 38, 38);
    else doc.setTextColor(16, 122, 87);
    doc.text(`Rs. ${fmt(bal)}`, pageW - 50, y + 14, { align: "right" });
    doc.setTextColor(17, 24, 39);
    y += 20;
  });
  if (orders.length === 0) {
    doc.setTextColor(107, 114, 128);
    doc.text("No orders.", 52, y + 14);
    doc.setTextColor(17, 24, 39);
    y += 20;
  }
  y += 14;

  // Receipts
  if (receipts && receipts.length > 0) {
    if (y > 700) {
      doc.addPage();
      y = 60;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Cash Receipts", 40, y);
    y += 12;
    doc.setFillColor(248, 250, 252);
    doc.rect(40, y, pageW - 80, 22, "F");
    doc.setFontSize(9);
    doc.text("Date", 52, y + 15);
    doc.text("Client", 140, y + 15);
    doc.text("Order No.", 300, y + 15);
    doc.text("Amount", pageW - 50, y + 15, { align: "right" });
    y += 22;
    doc.setFont("helvetica", "normal");
    receipts.forEach((r) => {
      if (y > 760) {
        doc.addPage();
        y = 60;
      }
      doc.text(formatDate(r.date).replace(" ", "-"), 52, y + 14);
      doc.text(String(r.client_name || "—").slice(0, 26), 140, y + 14);
      doc.text(String(r.order_number || "—"), 300, y + 14);
      doc.text(`Rs. ${fmt(r.amount)}`, pageW - 50, y + 14, { align: "right" });
      y += 20;
    });
  }

  const fname = `Adams-Ledger-${(customer.name || customer.phone).replace(/[^A-Za-z0-9]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.pdf`;
  savePdf(doc, fname);
}

// ============================================================
// HR — Pay slip
// ============================================================
function monthLabel(ym) {
  if (!ym || ym.length < 7) return ym || "—";
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
}

function amountInWords(amt) {
  // Simple INR words converter — sufficient for professional payslip footer.
  const n = Math.round(Number(amt || 0));
  if (!Number.isFinite(n) || n === 0) return "Zero Rupees Only";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (x) => x < 20 ? ones[x] : (tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : ""));
  const three = (x) => {
    if (x >= 100) {
      const h = Math.floor(x / 100);
      const r = x % 100;
      return ones[h] + " Hundred" + (r ? " " + two(r) : "");
    }
    return two(x);
  };
  const parts = [];
  let x = n;
  const crore = Math.floor(x / 10000000); x %= 10000000;
  const lakh = Math.floor(x / 100000); x %= 100000;
  const thousand = Math.floor(x / 1000); x %= 1000;
  const hundred = x;
  if (crore) parts.push(three(crore) + " Crore");
  if (lakh) parts.push(two(lakh) + " Lakh");
  if (thousand) parts.push(two(thousand) + " Thousand");
  if (hundred) parts.push(three(hundred));
  return parts.join(" ") + " Rupees Only";
}

function drawPayslip(doc, slip) {
  const { employee, month } = slip;
  const pageW = doc.internal.pageSize.getWidth();
  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(COMPANY.name, pageW - 40, 48, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const addrLines = doc.splitTextToSize(COMPANY.address, 260);
  doc.text(addrLines, pageW - 40, 64, { align: "right" });
  const ry = 64 + addrLines.length * 11;
  doc.text(`GSTIN: ${COMPANY.gst}`, pageW - 40, ry, { align: "right" });
  doc.text("MSME: UDYAM-KR-03-0783692", pageW - 40, ry + 11, { align: "right" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(1.5);
  doc.line(40, 120, pageW - 40, 120);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("PAYSLIP", pageW / 2, 148, { align: "center" });
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(71, 85, 105);
  doc.text(`For the month of ${monthLabel(month)}`, pageW / 2, 166, { align: "center" });

  let y = 196;
  doc.setTextColor(17, 24, 39);
  doc.setFontSize(9.5);
  const att = slip.attendance;
  const empRows = [
    ["Employee Name", employee.name || "—"],
    ["Employee ID", employee.employee_id || (employee.id || "").slice(0, 8).toUpperCase()],
    ["Designation", employee.designation || "—"],
    ["Date of Joining", employee.joining_date ? formatDate(employee.joining_date) : "—"],
    ["PAN", employee.pan || "—"],
    ...(employee.uan ? [["UAN", employee.uan]] : []),
  ];
  const payRows = [
    ["Pay Period", monthLabel(month)],
    ["Pay Date", slip.pay_date ? formatDate(slip.pay_date) : "—"],
    ["Payment Mode", "Bank Transfer"],
    ["Bank", employee.bank_name || "—"],
    ["Account No.", employee.bank_account || "—"],
    ["IFSC", employee.ifsc || "—"],
  ];
  const drawRows = (rows, x0, labelW, maxW) => {
    let ly = y;
    rows.forEach(([k, v]) => {
      doc.setFont("helvetica", "bold");
      doc.text(String(k), x0, ly);
      doc.setFont("helvetica", "normal");
      doc.text(doc.splitTextToSize(String(v), maxW)[0], x0 + labelW, ly);
      ly += 15;
    });
    return ly;
  };
  const leftEnd = drawRows(empRows, 40, 96, pageW / 2 - 150);
  const rightEnd = drawRows(payRows, pageW / 2 + 20, 80, pageW / 2 - 140);
  y = Math.max(leftEnd, rightEnd) + 4;
  if (att) {
    doc.setFillColor(248, 250, 252);
    doc.rect(40, y - 2, pageW - 80, 20, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    const parts = [`Days in month: ${slip.days_in_month}`, `Present: ${att.P + (att.HD || 0) * 0.5}`, `Leave: ${att.L}`, `Weekly off: ${att.WO}`, `Holidays: ${att.H}`, `Absent: ${att.A}`];
    doc.text(parts.join("     "), pageW / 2, y + 11, { align: "center" });
    doc.setTextColor(17, 24, 39);
    y += 28;
  } else {
    y += 10;
  }

  const lhX = 40, rhX = pageW / 2 + 10;
  const colW = pageW / 2 - 50;
  doc.setFillColor(17, 24, 39);
  doc.setTextColor(255, 255, 255);
  doc.rect(lhX, y, colW, 22, "F");
  doc.rect(rhX, y, colW, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("EARNINGS", lhX + 10, y + 15);
  doc.text("AMOUNT (Rs.)", lhX + colW - 10, y + 15, { align: "right" });
  doc.text("DEDUCTIONS", rhX + 10, y + 15);
  doc.text("AMOUNT (Rs.)", rhX + colW - 10, y + 15, { align: "right" });
  y += 22;
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  const earnings = (slip.earnings || []).filter((e) => Number(e.amount) > 0);
  const deductions = slip.deductions || [];
  const rowH = 18;
  for (let i = 0; i < Math.max(earnings.length, deductions.length, 1); i++) {
    doc.setDrawColor(226, 232, 240);
    doc.rect(lhX, y, colW, rowH);
    doc.rect(rhX, y, colW, rowH);
    if (earnings[i]) {
      doc.text(earnings[i].label, lhX + 10, y + 12);
      doc.text(fmt(earnings[i].amount), lhX + colW - 10, y + 12, { align: "right" });
    }
    if (deductions[i]) {
      doc.text(doc.splitTextToSize(deductions[i].label, colW - 90)[0], rhX + 10, y + 12);
      doc.text(fmt(deductions[i].amount), rhX + colW - 10, y + 12, { align: "right" });
    }
    y += rowH;
  }
  const totalEarnings = earnings.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalDeductions = deductions.reduce((s, e) => s + Number(e.amount || 0), 0);
  const net = totalEarnings - totalDeductions;
  doc.setFillColor(243, 244, 246);
  doc.rect(lhX, y, colW, 22, "F");
  doc.rect(rhX, y, colW, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.text("Gross Salary", lhX + 10, y + 15);
  doc.text(fmt(totalEarnings), lhX + colW - 10, y + 15, { align: "right" });
  doc.text("Total Deductions", rhX + 10, y + 15);
  doc.text(fmt(totalDeductions), rhX + colW - 10, y + 15, { align: "right" });
  y += 34;
  doc.setFillColor(220, 38, 38);
  doc.setTextColor(255, 255, 255);
  doc.rect(40, y, pageW - 80, 36, "F");
  doc.setFontSize(13);
  doc.text("NET PAY", 54, y + 23);
  doc.setFontSize(15);
  doc.text(`Rs. ${fmt(net)}`, pageW - 54, y + 23, { align: "right" });
  y += 50;
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9.5);
  doc.text(`In Words: ${amountInWords(net)}`, 40, y);
  y += 16;
  if (Number(slip.loan_outstanding_after) > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Loan balance after this month: Rs. ${fmt(slip.loan_outstanding_after)}`, 40, y);
    y += 16;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text("This is a computer-generated payslip and does not require a signature.", pageW / 2, y + 8, { align: "center" });
}

export function downloadPayslipPDF(slip) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  drawPayslip(doc, slip);
  savePdf(doc, `Adams-Payslip-${(slip.employee.name || "employee").replace(/[^A-Za-z0-9]+/g, "-")}-${slip.month}.pdf`);
}

export function downloadPayslipsPDF(slips, month) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  slips.forEach((s, i) => {
    if (i) doc.addPage();
    drawPayslip(doc, s);
  });
  savePdf(doc, `Adams-Payslips-${month}.pdf`);
}

const ATT_COLORS = { P: [220, 252, 231], A: [254, 226, 226], WO: [241, 245, 249], L: [219, 234, 254], H: [254, 243, 199], HD: [237, 233, 254] };

export function downloadAttendancePDF(data) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.text(`ADAMS — Attendance Register · ${monthLabel(data.month)}`, 28, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text("P Present · A Absent · WO Weekly off · L Leave · H Holiday · HD Half day", 28, 48);
  doc.text(`Printed ${formatDate(new Date().toISOString().slice(0, 10))}`, pageW - 28, 34, { align: "right" });
  const days = data.days;
  const nameW = 112, totW = 26 * 6;
  const cellW = (pageW - 56 - nameW - totW) / days.length;
  let y = 60;
  const head = () => {
    doc.setFillColor(17, 24, 39);
    doc.rect(28, y, pageW - 56, 26, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text("Employee", 32, y + 16);
    days.forEach((d, i) => {
      const x = 28 + nameW + i * cellW + cellW / 2;
      const dt = new Date(`${d}T00:00:00`);
      doc.text(String(dt.getDate()), x, y + 11, { align: "center" });
      doc.setFontSize(5.5);
      doc.text("SMTWTFS"[dt.getDay()], x, y + 20, { align: "center" });
      doc.setFontSize(7.5);
    });
    ["P", "A", "HD", "L", "WO", "H"].forEach((k, i) => doc.text(k, 28 + nameW + days.length * cellW + i * 26 + 13, y + 16, { align: "center" }));
    y += 26;
  };
  head();
  data.rows.forEach((r) => {
    if (y > doc.internal.pageSize.getHeight() - 40) {
      doc.addPage();
      y = 30;
      head();
    }
    doc.setDrawColor(226, 232, 240);
    doc.setTextColor(17, 24, 39);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.rect(28, y, nameW, 20);
    doc.text(doc.splitTextToSize(r.name || "—", nameW - 8)[0], 32, y + 9);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.text(r.employee_id || "", 32, y + 16);
    days.forEach((d, i) => {
      const x = 28 + nameW + i * cellW;
      const s = r.marks[d] || "";
      if (ATT_COLORS[s]) {
        doc.setFillColor(...ATT_COLORS[s]);
        doc.rect(x, y, cellW, 20, "FD");
      } else doc.rect(x, y, cellW, 20);
      doc.setFontSize(s.length > 1 ? 5.5 : 7);
      doc.text(s, x + cellW / 2, y + 12.5, { align: "center" });
    });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    const t = r.totals;
    [t.P, t.A, t.HD, t.L, t.WO, t.H].forEach((v, i) => {
      const x = 28 + nameW + days.length * cellW + i * 26;
      doc.rect(x, y, 26, 20);
      doc.text(String(v), x + 13, y + 12.5, { align: "center" });
    });
    y += 20;
  });
  y += 30;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Prepared by: ____________________", 28, y);
  doc.text("Authorised Signatory: ____________________", pageW - 28, y, { align: "right" });
  savePdf(doc, `Adams-Attendance-${data.month}.pdf`);
}

// ============================================================
// HR — Employment Verification Letter
// ============================================================
export function downloadEmploymentLetterPDF({ employee, letter_date, addressed_to, purpose }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  // Header
  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(COMPANY.name, pageW - 40, 48, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const addrLines = doc.splitTextToSize(COMPANY.address, 260);
  doc.text(addrLines, pageW - 40, 64, { align: "right" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(1.5);
  doc.line(40, 120, pageW - 40, 120);

  // Date + ref
  let y = 150;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Date: ${formatDate(letter_date)}`, pageW - 40, y, { align: "right" });
  const emp_ref = employee.employee_id || (employee.id || "").slice(0, 6).toUpperCase();
  doc.text(`Ref: ADAMS/HR/${new Date().getFullYear()}/${emp_ref}`, 40, y);

  y += 30;

  // Salutation: if 'Addressed to' is specified, use it as the salutation
  // block instead of the generic "To Whomsoever It May Concern".
  doc.setFont("helvetica", "bold");
  if (addressed_to && addressed_to.trim()) {
    doc.setFontSize(11);
    doc.text("To,", 40, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    const toLines = doc.splitTextToSize(addressed_to.trim(), pageW - 80);
    doc.text(toLines, 40, y);
    y += toLines.length * 14 + 8;
    // Subject line after the 'To' block
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Subject: Employment Verification", 40, y);
    y += 22;
  } else {
    doc.setFontSize(13);
    doc.text("TO WHOMSOEVER IT MAY CONCERN", pageW / 2, y, { align: "center" });
    y += 14;
    doc.setDrawColor(17, 24, 39);
    doc.setLineWidth(0.6);
    const titleW = 230;
    doc.line(pageW / 2 - titleW / 2, y, pageW / 2 + titleW / 2, y);
    y += 24;
  }

  // Body — standard wording approved by the user
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);

  const designation = employee.designation || "Staff";
  const employeeId = employee.employee_id || "—";
  const joining = employee.joining_date
    ? ` since ${formatDate(employee.joining_date)}`
    : "";
  const salaryLine = Number(employee.salary) > 0
    ? `The current gross monthly remuneration drawn is Rs. ${fmt(employee.salary)} (${amountInWords(employee.salary)}).`
    : "";
  const purposeLine = purpose
    ? `This letter is issued at the request of the employee for the purpose of ${purpose}.`
    : "This letter is issued at the request of the employee for their personal records.";

  const body = [
    `This is to confirm that ${employee.name || "—"} (Employee ID: ${employeeId}; Mobile: ${employee.mobile || "—"}) is employed full-time at ADAMS, Bangalore in the capacity of ${designation}${joining}.`,
    salaryLine,
    `During the tenure of employment, ${employee.name || "the employee"} has discharged their responsibilities diligently and professionally.`,
    purposeLine,
    "This letter is issued without any encumbrance, liability or obligation on the part of the company.",
    "Should you require any further verification, please feel free to contact our office at the details mentioned below.",
  ].filter(Boolean);

  body.forEach((para) => {
    const lines = doc.splitTextToSize(para, pageW - 80);
    doc.text(lines, 40, y);
    y += lines.length * 14 + 10;
  });

  // Contact block
  y += 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Contact Details:", 40, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.text("Email: director@adamsbangalore.com", 40, y);
  y += 13;
  doc.text("Phone: +91 92434 25500", 40, y);

  // Signature block
  y += 40;
  doc.setFont("helvetica", "normal");
  doc.text("Yours sincerely,", 40, y);
  y += 50;
  doc.setFont("helvetica", "bold");
  doc.text("Authorised Signatory", 40, y);
  y += 13;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("ADAMS — Human Resources", 40, y);

  // Footer note
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(
    "This document is digitally generated from the ADAMS HR system.",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 40,
    { align: "center" }
  );

  const fname = `Adams-Employment-Letter-${(employee.name || "employee").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`;
  savePdf(doc, fname);
}


// ============================================================
// Labour — Payments Summary (Round 95)
// ============================================================
// iPhone Safari blocks async `window.open` popups — the previous
// implementation opened a print-preview window and hit that block.
// This is a native jsPDF export that uses `savePdf(doc, )`, which routes
// through FileSaver and works on iOS 16+ (it triggers a same-tab
// download via an <a download> anchor click).
// Round 104 — Laundry vendor rate-card export. Simple portrait A4
// with vendor header + two-column item table.
export function downloadLaundryVendorRateCardPDF(vendor) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 155, 72);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 66);
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("LAUNDRY VENDOR RATE CARD", pageW / 2, 70, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text(
    `Generated ${new Date().toLocaleDateString("en-IN")}`,
    pageW / 2, 88, { align: "center" },
  );
  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  let y = 130;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.rect(40, y, pageW - 80, 56, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(vendor.name || "—", 52, y + 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(75, 85, 99);
  const meta = [];
  if (vendor.phone) meta.push(`Phone: ${vendor.phone}`);
  if (vendor.address) meta.push(vendor.address);
  if (meta.length) doc.text(meta.join("  ·  ").slice(0, 90), 52, y + 42);
  y += 72;

  const rateCard = vendor.item_rates || [];
  if (rateCard.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(148, 163, 184);
    doc.text("No items in this rate card.", pageW / 2, y + 40, { align: "center" });
  } else {
    doc.setFillColor(15, 23, 42);
    doc.rect(40, y, pageW - 80, 24, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("ITEM", 52, y + 16);
    doc.text("RATE (Rs.)", pageW - 52, y + 16, { align: "right" });
    y += 24;
    doc.setTextColor(17, 24, 39);
    rateCard.forEach((r, i) => {
      if (y > pageH - 60) { doc.addPage(); y = 60; }
      if (i % 2 !== 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(40, y, pageW - 80, 22, "F");
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 39);
      doc.text(String(r.item_type || "-").slice(0, 60), 52, y + 15);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(4, 120, 87);
      doc.text(`Rs. ${fmt(Number(r.rate || 0))}`, pageW - 52, y + 15, { align: "right" });
      y += 22;
    });
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(0.8);
    doc.line(40, y + 2, pageW - 40, y + 2);
    y += 12;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(
      `${rateCard.length} item${rateCard.length === 1 ? "" : "s"} in rate card`,
      pageW / 2, y + 10, { align: "center" },
    );
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(
    `Adams Bangalore  ·  ${COMPANY.web}`,
    pageW / 2, pageH - 30, { align: "center" },
  );
  const safeName = (vendor.name || "vendor").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  savePdf(doc, `Adams-Laundry-RateCard-${safeName}.pdf`);
}



export function downloadLabourPaymentsSummaryPDF({ month, bengaliRows = [], assameseRows = [] }) {
  // Round 105 — Landscape A4, 9-column table:
  // Name / Days Absent / Rate / Days Worked / Gross / Loan Ded /
  // Advance Taken (sum of "Record Advance" kind='advance') /
  // Recently Paid (latest "Mark Paid" kind='payment') / Balance.
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Split total_paid into advance-taken (kind='advance' → Record Advance)
  // vs latest payment (kind='payment' → Mark Paid). Uses row.advances[] which
  // /api/labour/summary returns already scoped to the selected month.
  const splitPayments = (r) => {
    const advs = Array.isArray(r.advances) ? r.advances : [];
    const advanceTaken = advs
      .filter((a) => a.kind === "advance")
      .reduce((s, a) => s + Number(a.amount || 0), 0);
    const payments = advs
      .filter((a) => a.kind === "payment" && Number(a.amount || 0) > 0)
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    const latestPay = payments[0] || null;
    return {
      advanceTaken,
      recentlyPaidAmount: latestPay ? Number(latestPay.amount || 0) : 0,
      recentlyPaidDate: latestPay ? String(latestPay.date || "").slice(0, 10) : "",
    };
  };

  // Header
  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("LABOUR PAYMENTS SUMMARY", pageW / 2, 70, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text(`Month: ${month}  ·  Generated ${new Date().toLocaleDateString("en-IN")}`, pageW / 2, 88, { align: "center" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  // Rollup KPIs (advance-taken vs paid split)
  const roll = (rows) => {
    let g = 0, ld = 0, advTaken = 0, recPaid = 0, b = 0;
    rows.forEach((r) => {
      const { advanceTaken, recentlyPaidAmount } = splitPayments(r);
      g += Number(r.gross_due || 0);
      ld += Number(r.loan_deduction || 0);
      advTaken += advanceTaken;
      recPaid += recentlyPaidAmount;
      b += Number(r.balance_due || 0);
    });
    return { g, ld, advTaken, recPaid, b };
  };
  const be = roll(bengaliRows);
  const as = roll(assameseRows);
  const grand = {
    g: be.g + as.g,
    ld: be.ld + as.ld,
    advTaken: be.advTaken + as.advTaken,
    recPaid: be.recPaid + as.recPaid,
    b: be.b + as.b,
  };

  let y = 130;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  const kpis = [
    ["GROSS DUE", `Rs. ${fmt(grand.g)}`, [17, 24, 39]],
    ["LOAN DEDUCTED", `Rs. ${fmt(grand.ld)}`, [180, 83, 9]],
    ["ADVANCE TAKEN", `Rs. ${fmt(grand.advTaken)}`, [180, 83, 9]],
    ["RECENTLY PAID", `Rs. ${fmt(grand.recPaid)}`, [4, 120, 87]],
    ["BALANCE DUE", `Rs. ${fmt(grand.b)}`, grand.b > 0 ? [185, 28, 28] : [4, 120, 87]],
  ];
  const cellW = (pageW - 80) / 5;
  kpis.forEach(([label, value, tone], i) => {
    const x = 40 + i * cellW;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.8);
    doc.roundedRect(x + 4, y, cellW - 8, 54, 4, 4);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(107, 114, 128);
    doc.text(label, x + 12, y + 16);
    doc.setFontSize(13);
    doc.setTextColor(...tone);
    doc.text(value, x + 12, y + 40);
  });
  y += 70;

  // Formula strip
  doc.setDrawColor(253, 230, 138);
  doc.setFillColor(254, 249, 195);
  doc.setLineWidth(0.6);
  doc.roundedRect(40, y, pageW - 80, 28, 3, 3, "FD");
  doc.setTextColor(120, 53, 15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(
    "Formula:  Rate x Days Worked + Addon = Gross Due  -  Loan Deduction  -  Advance Taken  -  Recently Paid  =  Balance Pending",
    pageW / 2, y + 18, { align: "center" }
  );
  y += 44;

  const renderGroup = (title, rows, roll) => {
    if (y > pageH - 140) { doc.addPage(); y = 60; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    doc.text(`${title}  -  ${rows.length} labourer${rows.length === 1 ? "" : "s"}`, 40, y);
    y += 14;
    if (rows.length === 0) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      doc.text("No records for this month.", 40, y + 4);
      y += 24;
      return;
    }
    // Column x-positions for landscape A4 (~842pt wide, 762 usable).
    // Round 105 — 9 columns; monthly-addon labourers get a bold name only
    // (no full-row highlight), keeping the sheet clean.
    const COL_NAME = 48;
    const COL_ABS = 235;       // right-aligned
    const COL_RATE = 310;      // right-aligned
    const COL_WORKED = 385;    // right-aligned  (NEW)
    const COL_GROSS = 465;     // right-aligned
    const COL_LOAN = 540;      // right-aligned
    const COL_ADV = 620;       // right-aligned
    const COL_LAST = 715;      // right-aligned (amount + date on line 2)
    const COL_BAL = pageW - 48; // right-aligned

    // Header row
    doc.setFillColor(248, 250, 252);
    doc.rect(40, y, pageW - 80, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(75, 85, 99);
    doc.text("NAME", COL_NAME, y + 15);
    doc.text("DAYS ABS", COL_ABS, y + 15, { align: "right" });
    doc.text("RATE", COL_RATE, y + 15, { align: "right" });
    doc.text("DAYS WORKED", COL_WORKED, y + 15, { align: "right" });
    doc.text("GROSS", COL_GROSS, y + 15, { align: "right" });
    doc.text("LOAN DED", COL_LOAN, y + 15, { align: "right" });
    doc.text("ADVANCE TAKEN", COL_ADV, y + 15, { align: "right" });
    doc.text("RECENTLY PAID", COL_LAST, y + 15, { align: "right" });
    doc.text("BALANCE", COL_BAL, y + 15, { align: "right" });
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(17, 24, 39);
    rows.forEach((r) => {
      if (y > pageH - 60) { doc.addPage(); y = 60; }
      const rate = Number(r.daily_rate || 0);
      const daysAbs = Number(r.days_absent || 0);
      const daysWorked = Number(r.days_worked || 0);
      const gross = Number(r.gross_due || 0);
      const ld = Number(r.loan_deduction || 0);
      const bal = Number(r.balance_due || 0);
      const addon = Number(r.monthly_addon || 0);
      const { advanceTaken, recentlyPaidAmount, recentlyPaidDate } = splitPayments(r);
      // Row height — 2-line "Recently paid" (amount + date) needs 22pt.
      const rowH = 22;
      // Round 105 — Removed amber row highlight; only the NAME is bolded
      // for monthly-addon labourers so the sheet stays clean.
      // Name (bold amber if addon, plus small (+addon) note beneath)
      if (addon > 0) {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(146, 64, 14);
      } else {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(17, 24, 39);
      }
      doc.text(String(r.name || "-").slice(0, 26), COL_NAME, y + 14);
      if (addon > 0) {
        doc.setFontSize(7);
        doc.setTextColor(146, 64, 14);
        doc.text(`(+ Rs.${fmt(addon)} addon)`, COL_NAME, y + 20);
        doc.setFontSize(9);
      } else if (r.archived) {
        // Round 125.8 — Archive note under the name for labourers
        // terminated during this month. Their amounts still count
        // toward monthly totals, but the note signals to payroll
        // that they left mid-month.
        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        const dayLabel = r.archived_day ? ` on ${String(r.archived_day).padStart(2, "0")}` : "";
        doc.text(`Archived${dayLabel}`, COL_NAME, y + 20);
        doc.setFontSize(9);
      }
      // Days Absent
      doc.setFont("helvetica", "normal");
      doc.setTextColor(daysAbs > 0 ? 185 : 148, daysAbs > 0 ? 28 : 163, daysAbs > 0 ? 28 : 184);
      doc.text(daysAbs > 0 ? String(daysAbs) : "0", COL_ABS, y + 14, { align: "right" });
      // Rate
      doc.setTextColor(17, 24, 39);
      doc.text(`Rs.${fmt(rate)}`, COL_RATE, y + 14, { align: "right" });
      // Days Worked
      doc.text(String(daysWorked), COL_WORKED, y + 14, { align: "right" });
      // Gross
      doc.text(`Rs.${fmt(gross)}`, COL_GROSS, y + 14, { align: "right" });
      // Loan Ded
      if (ld > 0) {
        doc.setTextColor(180, 83, 9);
        doc.text(`-${fmt(ld)}`, COL_LOAN, y + 14, { align: "right" });
      } else {
        doc.setTextColor(148, 163, 184);
        doc.text("-", COL_LOAN, y + 14, { align: "right" });
      }
      // Advance Taken (total of kind='advance' i.e. Record Advance entries)
      if (advanceTaken > 0) {
        doc.setTextColor(180, 83, 9);
        doc.text(`Rs.${fmt(advanceTaken)}`, COL_ADV, y + 14, { align: "right" });
      } else {
        doc.setTextColor(148, 163, 184);
        doc.text("-", COL_ADV, y + 14, { align: "right" });
      }
      // Recently Paid — latest Mark Paid amount + date on line 2
      if (recentlyPaidAmount > 0) {
        doc.setTextColor(4, 120, 87);
        doc.setFont("helvetica", "bold");
        doc.text(`Rs.${fmt(recentlyPaidAmount)}`, COL_LAST, y + 14, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(107, 114, 128);
        doc.text(recentlyPaidDate, COL_LAST, y + 20, { align: "right" });
        doc.setFontSize(9);
      } else {
        doc.setTextColor(148, 163, 184);
        doc.text("-", COL_LAST, y + 14, { align: "right" });
      }
      // Balance (bold, red if positive)
      doc.setTextColor(bal > 0 ? 185 : 4, bal > 0 ? 28 : 120, bal > 0 ? 28 : 87);
      doc.setFont("helvetica", "bold");
      doc.text(`Rs.${fmt(bal)}`, COL_BAL, y + 14, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setTextColor(17, 24, 39);
      y += rowH;
    });
    // Totals row
    if (y > pageH - 60) { doc.addPage(); y = 60; }
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(0.8);
    doc.line(40, y + 2, pageW - 40, y + 2);
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(15, 23, 42);
    doc.text("Totals", COL_NAME, y + 14);
    doc.text(`Rs.${fmt(roll.g)}`, COL_GROSS, y + 14, { align: "right" });
    doc.setTextColor(180, 83, 9);
    doc.text(`-${fmt(roll.ld)}`, COL_LOAN, y + 14, { align: "right" });
    doc.setTextColor(180, 83, 9);
    doc.text(`Rs.${fmt(roll.advTaken)}`, COL_ADV, y + 14, { align: "right" });
    doc.setTextColor(4, 120, 87);
    doc.text(`Rs.${fmt(roll.recPaid)}`, COL_LAST, y + 14, { align: "right" });
    doc.setTextColor(roll.b > 0 ? 185 : 4, roll.b > 0 ? 28 : 120, roll.b > 0 ? 28 : 87);
    doc.text(`Rs.${fmt(roll.b)}`, COL_BAL, y + 14, { align: "right" });
    doc.setTextColor(17, 24, 39);
    y += 30;
  };

  renderGroup("Bengali Labour", bengaliRows, be);
  renderGroup("Assamese Labour", assameseRows, as);

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(
    `Adams Bangalore  ·  ${COMPANY.web}`,
    pageW / 2, pageH - 30, { align: "center" }
  );

  savePdf(doc, `Adams-Labour-Payments-Summary-${month}.pdf`);
}


// ============================================================
// Laundry — Monthly report (Round 95)
// ============================================================
export function downloadLaundryReportPDF(summary, month, opts = {}) {
  const { vendorName = "" } = opts;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("LAUNDRY REPORT", pageW / 2, 66, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text(`Period: ${month}  ·  Generated ${new Date().toLocaleDateString("en-IN")}`, pageW / 2, 84, { align: "center" });
  if (vendorName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(193, 39, 45);
    doc.text(`Vendor: ${vendorName}`, pageW / 2, 100, { align: "center" });
  }

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, vendorName ? 110 : 100, pageW - 40, vendorName ? 110 : 100);

  // KPIs
  let y = vendorName ? 128 : 118;
  const carryFwd = Number(summary.total_carry_forward || 0);
  const kpis = [
    ["TOTAL PIECES", String(summary.total_pieces || 0), [17, 24, 39]],
    ["TOTAL AMOUNT", `Rs. ${fmt(summary.total_amount || 0)}`, [17, 24, 39]],
    ["PAID", `Rs. ${fmt(summary.total_paid || 0)}`, [4, 120, 87]],
    ["OUTSTANDING", `Rs. ${fmt(summary.total_balance || 0)}`, summary.total_balance > 0 ? [185, 28, 28] : [4, 120, 87]],
  ];
  const cellW = (pageW - 80) / 4;
  kpis.forEach(([label, value, tone], i) => {
    const x = 40 + i * cellW;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.8);
    doc.roundedRect(x + 4, y, cellW - 8, 54, 4, 4);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(107, 114, 128);
    doc.text(label, x + 12, y + 16);
    doc.setFontSize(13);
    doc.setTextColor(...tone);
    doc.text(value, x + 12, y + 40);
  });
  y += 62;
  if (carryFwd > 0) {
    // Explicit carry-forward call-out under the KPI strip.
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(180, 83, 9);
    doc.text(
      `Includes Rs.${fmt(carryFwd)} carried forward from prior period(s)  ·  ${summary.carry_forward_count || 0} open entr${(summary.carry_forward_count || 0) === 1 ? "y" : "ies"}`,
      40, y + 8,
    );
    doc.setTextColor(17, 24, 39);
    doc.setFont("helvetica", "normal");
    y += 20;
  }
  y += 12;

  // Per-vendor summary
  const vendorRows = summary.by_vendor || [];
  if (y > pageH - 120) { doc.addPage(); y = 60; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  doc.text("Per-vendor summary", 40, y);
  y += 14;
  if (vendorRows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text("No vendor entries.", 40, y + 4);
    y += 24;
  } else {
    doc.setFillColor(248, 250, 252);
    doc.rect(40, y, pageW - 80, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(75, 85, 99);
    doc.text("VENDOR", 48, y + 15);
    doc.text("PIECES", 280, y + 15, { align: "right" });
    doc.text("AMOUNT", 360, y + 15, { align: "right" });
    doc.text("PAID", 440, y + 15, { align: "right" });
    doc.text("BALANCE", pageW - 48, y + 15, { align: "right" });
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(17, 24, 39);
    vendorRows.forEach((v) => {
      if (y > pageH - 60) { doc.addPage(); y = 60; }
      doc.text(String(v.vendor_name || "-").slice(0, 34), 48, y + 14);
      doc.text(String(v.pieces || 0), 280, y + 14, { align: "right" });
      doc.text(`Rs.${fmt(v.amount || 0)}`, 360, y + 14, { align: "right" });
      doc.setTextColor(4, 120, 87);
      doc.text(`Rs.${fmt(v.paid_amount || 0)}`, 440, y + 14, { align: "right" });
      const b = Number(v.balance || 0);
      doc.setTextColor(b > 0 ? 185 : 4, b > 0 ? 28 : 120, b > 0 ? 28 : 87);
      doc.setFont("helvetica", "bold");
      doc.text(`Rs.${fmt(b)}`, pageW - 48, y + 14, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setTextColor(17, 24, 39);
      y += 18;
    });
    y += 12;
  }

  // Entries — Round 122 rewrite. Group entries per-vendor so the
  // dhobi vendors receive a section each in the consolidated PDF
  // (instead of a chronological "all entries" table). Summary + KPIs
  // remain shared at the top.
  const entries = summary.entries || [];
  if (y > pageH - 120) { doc.addPage(); y = 60; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  doc.text(`Entries by vendor  -  ${entries.length}`, 40, y);
  y += 14;
  if (entries.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text("No entries this month.", 40, y + 4);
  } else {
    // Group entries by vendor.
    const byVendor = new Map();
    for (const e of entries) {
      const key = e.vendor_id || e.vendor_name || "_unknown";
      if (!byVendor.has(key)) {
        byVendor.set(key, { name: e.vendor_name || "Unknown vendor", items: [] });
      }
      byVendor.get(key).items.push(e);
    }
    // Render each vendor as its own section.
    const vendorGroups = Array.from(byVendor.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );

    vendorGroups.forEach((grp, idx) => {
      // Section title bar per vendor
      if (y > pageH - 100) { doc.addPage(); y = 60; }
      doc.setFillColor(30, 41, 59);
      doc.rect(40, y, pageW - 80, 22, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text(String(grp.name).toUpperCase(), 48, y + 15);
      // Per-vendor totals in the bar
      const vTotal = grp.items.reduce((s, e) => s + Number(e.amount || 0), 0);
      const vPaid = grp.items.reduce((s, e) => s + Number(e.amount_paid != null ? e.amount_paid : (e.paid ? e.amount : 0)), 0);
      const vBal = vTotal - vPaid;
      doc.setFontSize(8);
      doc.text(
        `${grp.items.length} entries  ·  Rs.${fmt(vTotal)}  ·  Paid Rs.${fmt(vPaid)}  ·  Bal Rs.${fmt(vBal)}`,
        pageW - 48,
        y + 15,
        { align: "right" }
      );
      y += 22;

      // Column headers
      doc.setFillColor(248, 250, 252);
      doc.rect(40, y, pageW - 80, 20, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(75, 85, 99);
      doc.text("DATE", 48, y + 14);
      doc.text("ITEM", 130, y + 14);
      doc.text("PCS", 320, y + 14, { align: "right" });
      doc.text("RATE", 380, y + 14, { align: "right" });
      doc.text("AMOUNT", pageW - 90, y + 14, { align: "right" });
      doc.text("STATUS", pageW - 48, y + 14, { align: "right" });
      y += 20;

      // Rows
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(17, 24, 39);
      grp.items.forEach((e) => {
        if (y > pageH - 60) { doc.addPage(); y = 60; }
        doc.text(String(e.date || "-"), 48, y + 14);
        doc.text(String(e.item_type || "-").slice(0, 30), 130, y + 14);
        doc.text(String(e.pieces || 0), 320, y + 14, { align: "right" });
        doc.text(`Rs.${fmt(e.rate_per_piece || 0)}`, 380, y + 14, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text(`Rs.${fmt(e.amount || 0)}`, pageW - 90, y + 14, { align: "right" });
        doc.setFont("helvetica", "normal");
        // Round 126.15 — reflect partial payments in the STATUS column.
        const bal = Number(e.balance_due != null ? e.balance_due : (e.paid ? 0 : e.amount));
        const paidPart = Number(e.amount_paid || 0);
        if (e.paid || bal <= 0) {
          doc.setTextColor(4, 120, 87);
          doc.text("Paid", pageW - 48, y + 14, { align: "right" });
        } else if (paidPart > 0) {
          doc.setTextColor(30, 64, 175);
          doc.text(`Bal Rs.${fmt(bal)}`, pageW - 48, y + 14, { align: "right" });
        } else {
          doc.setTextColor(185, 28, 28);
          doc.text("Unpaid", pageW - 48, y + 14, { align: "right" });
        }
        doc.setTextColor(17, 24, 39);
        y += 16;
      });
      y += 10; // spacer between vendor blocks
    });
  }

  // Round 126.15 — Carry-forward from prior periods (rendered AFTER
  // per-vendor entries so admins can see what's still hanging).
  const cfEntries = summary.carry_forward_entries || [];
  if (cfEntries.length > 0) {
    if (y > pageH - 120) { doc.addPage(); y = 60; }
    doc.setFillColor(254, 243, 199);
    doc.rect(40, y, pageW - 80, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(146, 64, 14);
    doc.text("CARRIED FORWARD FROM PRIOR PERIOD", 48, y + 15);
    doc.setFontSize(8);
    const cfTotal = cfEntries.reduce((s, e) => s + Number(e.balance_due || 0), 0);
    doc.text(
      `${cfEntries.length} open entr${cfEntries.length === 1 ? "y" : "ies"}  ·  Bal Rs.${fmt(cfTotal)}`,
      pageW - 48, y + 15, { align: "right" },
    );
    y += 22;
    doc.setFillColor(248, 250, 252);
    doc.rect(40, y, pageW - 80, 18, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(75, 85, 99);
    doc.text("DATE", 48, y + 13);
    doc.text("VENDOR", 130, y + 13);
    doc.text("ITEM", 250, y + 13);
    doc.text("AMOUNT", pageW - 130, y + 13, { align: "right" });
    doc.text("BALANCE", pageW - 48, y + 13, { align: "right" });
    y += 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(17, 24, 39);
    cfEntries.forEach((e) => {
      if (y > pageH - 60) { doc.addPage(); y = 60; }
      doc.text(String(e.date || "-"), 48, y + 13);
      doc.text(String(e.vendor_name || "-").slice(0, 22), 130, y + 13);
      doc.text(String(e.item_type || "-").slice(0, 22), 250, y + 13);
      doc.text(`Rs.${fmt(e.amount || 0)}`, pageW - 130, y + 13, { align: "right" });
      doc.setTextColor(185, 28, 28);
      doc.setFont("helvetica", "bold");
      doc.text(`Rs.${fmt(e.balance_due || 0)}`, pageW - 48, y + 13, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setTextColor(17, 24, 39);
      y += 15;
    });
    y += 10;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(`Adams Bangalore  ·  ${COMPANY.web}`, pageW / 2, pageH - 30, { align: "center" });

  const filenameTag = (vendorName
    ? `${vendorName.replace(/[^A-Za-z0-9]+/g, "-")}-${month}`
    : month).replace(/[^A-Za-z0-9_-]+/g, "-").replace(/-+/g, "-");
  savePdf(doc, `Adams-Laundry-Report-${filenameTag}.pdf`);
}

// ============================================================
// Calendar — Weekly summary (Round 96)
// ============================================================
// One-page PDF for the operations team showing the 7-day rundown of
// deliveries + returns. Each day is tagged Free/Moderate/Busy based on
// combined load relative to the week's peak. iOS-safe via savePdf(doc, ).
export function downloadCalendarWeekSummaryPDF({ weekStart, days = [], weekLabel = "" }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Header
  try {
    doc.addImage(ADAMS_LOGO, "JPEG", 40, 28, 165, 76);
  } catch (e) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", 40, 70);
  }
  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("WEEKLY OPERATIONS SUMMARY", pageW / 2, 70, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text(`${weekLabel}  ·  Generated ${new Date().toLocaleString("en-IN")}`, pageW / 2, 88, { align: "center" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(40, 110, pageW - 40, 110);

  // Weekly roll-up strip
  const totals = days.reduce(
    (acc, d) => {
      acc.del += d.deliveries;
      acc.ret += d.returns;
      return acc;
    },
    { del: 0, ret: 0 },
  );
  const maxLoad = Math.max(1, ...days.map((d) => d.deliveries + d.returns));
  const busyDays = days.filter((d) => d.deliveries + d.returns >= Math.ceil(maxLoad * 0.75) && d.deliveries + d.returns > 0).length;
  const freeDays = days.filter((d) => d.deliveries + d.returns === 0).length;

  let y = 130;
  const kpis = [
    ["DELIVERIES", String(totals.del), [17, 24, 39]],
    ["RETURNS", String(totals.ret), [17, 24, 39]],
    ["BUSY DAYS", `${busyDays}/7`, [185, 28, 28]],
    ["FREE DAYS", `${freeDays}/7`, [4, 120, 87]],
  ];
  const cellW = (pageW - 80) / 4;
  kpis.forEach(([label, value, tone], i) => {
    const x = 40 + i * cellW;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.8);
    doc.roundedRect(x + 4, y, cellW - 8, 54, 4, 4);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(107, 114, 128);
    doc.text(label, x + 12, y + 16);
    doc.setFontSize(14);
    doc.setTextColor(...tone);
    doc.text(value, x + 12, y + 40);
  });
  y += 76;

  // Per-day table
  doc.setFillColor(248, 250, 252);
  doc.rect(40, y, pageW - 80, 26, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(75, 85, 99);
  doc.text("DAY", 48, y + 17);
  doc.text("DATE", 130, y + 17);
  doc.text("DELIVERIES", 285, y + 17, { align: "right" });
  doc.text("RETURNS", 380, y + 17, { align: "right" });
  doc.text("TOTAL", 455, y + 17, { align: "right" });
  doc.text("LOAD", pageW - 48, y + 17, { align: "right" });
  y += 26;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  days.forEach((d) => {
    const total = d.deliveries + d.returns;
    let label, tone;
    if (total === 0) {
      label = "Free"; tone = [4, 120, 87];
    } else if (total >= Math.ceil(maxLoad * 0.75)) {
      label = "Busy"; tone = [185, 28, 28];
    } else if (total >= Math.ceil(maxLoad * 0.4)) {
      label = "Moderate"; tone = [180, 83, 9];
    } else {
      label = "Light"; tone = [4, 120, 87];
    }
    // Zebra
    if ((days.indexOf(d) % 2) === 1) {
      doc.setFillColor(252, 252, 254);
      doc.rect(40, y, pageW - 80, 22, "F");
    }
    doc.setFont("helvetica", d.isToday ? "bold" : "normal");
    doc.setTextColor(17, 24, 39);
    doc.text(d.weekday, 48, y + 15);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99);
    doc.text(d.dateLabel, 130, y + 15);
    doc.setTextColor(17, 24, 39);
    doc.text(String(d.deliveries), 285, y + 15, { align: "right" });
    doc.text(String(d.returns), 380, y + 15, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.text(String(total), 455, y + 15, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...tone);
    doc.text(label.toUpperCase(), pageW - 48, y + 15, { align: "right" });
    doc.setTextColor(17, 24, 39);
    y += 22;
    if (y > pageH - 100) { doc.addPage(); y = 60; }
  });

  // Legend
  y += 12;
  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(253, 246, 236);
  doc.roundedRect(40, y, pageW - 80, 34, 4, 4, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(120, 53, 15);
  doc.text("LEGEND", 48, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(75, 85, 99);
  doc.text(
    "Load thresholds relative to this week's peak — Free (0 orders) · Light (<40%) · Moderate (40–75%) · Busy (>=75%).",
    48, y + 27,
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(`Adams Bangalore  ·  ${COMPANY.web}`, pageW / 2, pageH - 30, { align: "center" });

  // Round 126.8 — Per-day breakdown: one page per active day with two
  // tables (Deliveries above, Returns below) covering name, phone,
  // address and a quick items list. Empty days are skipped to keep
  // the PDF compact.
  const activeDays = (days || []).filter(
    (d) => (d.deliveryOrders || []).length > 0 || (d.returnOrders || []).length > 0,
  );
  const itemsSummary = (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    const parts = items
      .filter((it) => (it?.name || "").trim())
      .map((it) => `${it.name} × ${Number(it.quantity || 1)}`);
    if (parts.length === 0) return "—";
    // Keep the line print-friendly: at most ~120 chars, then ellipsis.
    const joined = parts.join(", ");
    return joined.length > 240 ? joined.slice(0, 237) + "…" : joined;
  };
  const drawBreakdownTable = (title, orders, startY, tone) => {
    // Header strip
    doc.setFillColor(...tone.bg);
    doc.rect(40, startY, pageW - 80, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...tone.fg);
    doc.text(title.toUpperCase(), 48, startY + 15);
    doc.setTextColor(...tone.fg);
    doc.text(`${orders.length}`, pageW - 48, startY + 15, { align: "right" });
    let ty = startY + 22;
    // Column header
    doc.setFillColor(248, 250, 252);
    doc.rect(40, ty, pageW - 80, 18, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(107, 114, 128);
    doc.text("ORDER #", 46, ty + 12);
    doc.text("CUSTOMER", 105, ty + 12);
    doc.text("PHONE", 235, ty + 12);
    doc.text("ADDRESS", 310, ty + 12);
    doc.text("ITEMS", 445, ty + 12);
    ty += 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    orders.forEach((o, idx) => {
      // Row zebra
      if (idx % 2 === 1) {
        doc.setFillColor(252, 252, 254);
        doc.rect(40, ty, pageW - 80, 30, "F");
      }
      const orderNo = String(o?.order_number || o?.id?.slice(0, 6) || "").slice(0, 10);
      const custName = String(o?.customer_name || "").slice(0, 24);
      const phone = String(o?.customer_phone || o?.phone || "");
      const address = String(o?.address || "");
      const items = itemsSummary(o);
      doc.setTextColor(17, 24, 39);
      doc.setFont("helvetica", "bold");
      doc.text(orderNo, 46, ty + 12);
      doc.setFont("helvetica", "normal");
      doc.text(custName, 105, ty + 12);
      doc.setTextColor(75, 85, 99);
      doc.text(phone, 235, ty + 12);
      // Wrap address to 2 lines max within its column width.
      const addrLines = doc.splitTextToSize(address, 130).slice(0, 2);
      doc.text(addrLines, 310, ty + 12);
      // Wrap items to 2 lines max.
      const itemLines = doc.splitTextToSize(items, 105).slice(0, 2);
      doc.text(itemLines, 445, ty + 12);
      ty += 30;
      if (ty > pageH - 60) {
        doc.addPage();
        ty = 60;
      }
    });
    // Row separator line
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.4);
    doc.line(40, ty, pageW - 80 + 40, ty);
    return ty + 6;
  };
  activeDays.forEach((d) => {
    doc.addPage();
    let py = 50;
    // Day title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(17, 24, 39);
    doc.text(`${d.weekday}, ${d.dateLabel}`, 40, py);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    doc.text(
      `${d.deliveries} delivery · ${d.returns} return · ${d.total} total`,
      pageW - 40, py, { align: "right" },
    );
    doc.setDrawColor(220, 38, 38);
    doc.setLineWidth(1.2);
    doc.line(40, py + 6, pageW - 40, py + 6);
    py += 22;
    // Deliveries first
    if ((d.deliveryOrders || []).length > 0) {
      py = drawBreakdownTable(
        `Deliveries · ${d.dateLabel}`,
        d.deliveryOrders,
        py,
        { bg: [254, 226, 226], fg: [153, 27, 27] },
      );
      py += 8;
    }
    // Returns below
    if ((d.returnOrders || []).length > 0) {
      py = drawBreakdownTable(
        `Returns · ${d.dateLabel}`,
        d.returnOrders,
        py,
        { bg: [220, 252, 231], fg: [22, 101, 52] },
      );
    }
    // Footer
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text(`Adams Bangalore  ·  ${COMPANY.web}`, pageW / 2, pageH - 30, { align: "center" });
  });

  const isoStart = weekStart.toISOString().slice(0, 10);
  savePdf(doc, `Adams-Weekly-Summary-${isoStart}.pdf`);
}



// ────────────────────────────────────────────────────────────────
// Round 97 — Vendor engagement PDF
// A printable / shareable brief covering the client, date, handover
// time, location, agreed amount, and the detailed scope of work with
// embedded reference photos.
// ────────────────────────────────────────────────────────────────
export function downloadEngagementPDF(engagement, { vendorName = "", vendorPhone = "" } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  // Round 97.3 — Convert 24h "HH:MM" (or "H:MM") into a friendly
  // "h:MM AM/PM" string. Falls back to the raw value if input is
  // unexpected.
  const formatHandover = (raw) => {
    if (!raw) return "—";
    const m = /^(\d{1,2}):(\d{2})/.exec(String(raw).trim());
    if (!m) return String(raw);
    let h = parseInt(m[1], 10);
    const mm = m[2];
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${mm} ${ampm}`;
  };

  // Round 103 — Format event date as "12th November 2026".
  const formatEventDate = (raw) => {
    if (!raw) return "—";
    const s = String(raw).trim();
    // Accept "YYYY-MM-DD" or a Date-parsable string.
    let d = null;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    else d = new Date(s);
    if (isNaN(d.getTime())) return s;
    const day = d.getDate();
    const ordinal =
      day >= 11 && day <= 13
        ? "th"
        : day % 10 === 1
          ? "st"
          : day % 10 === 2
            ? "nd"
            : day % 10 === 3
              ? "rd"
              : "th";
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    return `${day}${ordinal} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
  };

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", margin, 28, 155, 72);
  } catch {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", margin, 66);
  }

  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  const rightX = pageW - margin;
  let ry = 40;
  doc.text("Registered Office", rightX, ry, { align: "right" });
  ry += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const addr = doc.splitTextToSize(COMPANY.address, 260);
  doc.text(addr, rightX, ry, { align: "right" });
  ry += addr.length * 11 + 3;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(`GSTIN: ${COMPANY.gst}`, rightX, ry, { align: "right" });

  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(2);
  doc.line(margin, 108, pageW - margin, 108);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("VENDOR WORK BRIEF", pageW / 2, 132, { align: "center" });

  let y = 160;

  // Round 97.3 — Vendor block: name + phone up top so drivers can
  // reach the vendor without hunting through the ops chat.
  if (vendorName || vendorPhone) {
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.rect(margin, y, pageW - 2 * margin, 44, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text("ISSUED TO VENDOR", margin + 10, y + 15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    doc.text(vendorName || "—", margin + 10, y + 33);
    if (vendorPhone) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      doc.text(`Contact: ${vendorPhone}`, rightX - 10, y + 33, { align: "right" });
    }
    y += 60;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(17, 24, 39);
  doc.text(engagement.client_name || "—", margin, y);
  y += 22;

  // Info grid: event date | handover time | location | amount
  // Round 103 — taller cells + subtle bg tint on top for readability.
  const gridH = 74;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.rect(margin, y, pageW - 2 * margin, gridH, "FD");
  // top strip for labels
  doc.setFillColor(248, 250, 252);
  doc.rect(margin, y, pageW - 2 * margin, 26, "F");
  const col = (pageW - 2 * margin) / 4;
  const labels = ["Event Date", "Handover Time", "Location", "Amount Agreed"];
  const values = [
    formatEventDate(engagement.event_date),
    formatHandover(engagement.handover_time),
    (engagement.location || "—").slice(0, 40),
    `Rs. ${fmt(engagement.amount_agreed || 0)}`,
  ];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  labels.forEach((l, i) => doc.text(l.toUpperCase(), margin + col * i + 12, y + 17));
  // vertical separators
  doc.setDrawColor(226, 232, 240);
  for (let i = 1; i < 4; i++) {
    doc.line(margin + col * i, y + 4, margin + col * i, y + gridH - 4);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  values.forEach((v, i) => {
    const wrapped = doc.splitTextToSize(String(v), col - 24);
    doc.text(wrapped, margin + col * i + 12, y + 48);
  });
  y += gridH + 22;

  // Scope of work
  const scope = engagement.scope_items || [];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  doc.text("SCOPE OF WORK", margin, y);
  y += 18;

  const ensureRoom = (needed) => {
    if (y + needed > pageH - 60) {
      doc.addPage();
      y = margin;
    }
  };

  if (scope.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text("No scope items provided.", margin, y);
    y += 20;
  } else {
    scope.forEach((si, idx) => {
      ensureRoom(60);
      // Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(17, 24, 39);
      const headerText = `${idx + 1}. ${si.header || "Untitled"}`;
      const headerLines = doc.splitTextToSize(headerText, pageW - 2 * margin);
      doc.text(headerLines, margin, y);
      y += headerLines.length * 14 + 2;
      // Description
      if (si.description) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(51, 65, 85);
        const descLines = doc.splitTextToSize(si.description, pageW - 2 * margin - 12);
        ensureRoom(descLines.length * 12 + 6);
        doc.text(descLines, margin + 12, y);
        y += descLines.length * 12 + 4;
      }
      // Images — Round 103: larger 2-per-row thumbnails with a
      // subtle rounded frame + border for a premium feel.
      const imgs = (si.images || []).filter(Boolean);
      if (imgs.length > 0) {
        const perRow = 2;
        const gap = 14;
        const availW = pageW - 2 * margin;
        const thumbW = (availW - gap * (perRow - 1)) / perRow;
        const thumbH = thumbW * 0.68; // 3:2 ish aspect
        imgs.forEach((src, i) => {
          const rowIdx = Math.floor(i / perRow);
          const colIdx = i % perRow;
          if (colIdx === 0) {
            ensureRoom(thumbH + 20);
          }
          const x = margin + colIdx * (thumbW + gap);
          const yy = y + rowIdx * (thumbH + gap + 4);
          // Border/frame under the image
          doc.setFillColor(248, 250, 252);
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.5);
          doc.rect(x - 2, yy - 2, thumbW + 4, thumbH + 4, "FD");
          try {
            const fmtKind = src.startsWith("data:image/png") ? "PNG" : "JPEG";
            doc.addImage(src, fmtKind, x, yy, thumbW, thumbH, undefined, "FAST");
          } catch {
            /* skip broken image silently */
          }
        });
        const rows = Math.ceil(imgs.length / perRow);
        y += rows * (thumbH + gap + 4);
      }
      y += 14;
    });
  }

  // Notes
  if (engagement.notes && String(engagement.notes).trim()) {
    ensureRoom(50);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.text("NOTES", margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    const noteLines = doc.splitTextToSize(String(engagement.notes), pageW - 2 * margin);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 12 + 8;
  }

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(
    `Generated ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}  ·  ${COMPANY.web}`,
    pageW / 2,
    pageH - 24,
    { align: "center" }
  );

  const safe = (engagement.client_name || "engagement").replace(/[^a-z0-9]+/gi, "_");
  savePdf(doc, `Adams-Vendor-Brief-${safe}.pdf`);
}

// ────────────────────────────────────────────────────────────────
// Round 97.3 — Driver Trip Sheet PDF
// A single-vehicle route card for the driver:
//   Vehicle no · Driver name · Dispatch time · Date
//   Numbered list of stops with customer name, phone, address
// Deliberately lean — no amounts, no items — so the driver isn't
// distracted by numbers they don't need on the road.
// ────────────────────────────────────────────────────────────────
export function downloadTripSheetPDF(trip, { date, totalKm = null, fuelLitres = null } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", margin, 28, 130, 60);
  } catch {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", margin, 60);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text("DRIVER TRIP SHEET", pageW - margin, 46, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  if (date) doc.text(new Date(date + "T00:00:00").toDateString(), pageW - margin, 60, { align: "right" });

  // Divider
  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(1.6);
  doc.line(margin, 98, pageW - margin, 98);

  // Meta grid — vehicle, driver, dispatch time
  let y = 118;
  const cellW = (pageW - 2 * margin) / 3;
  const meta = [
    ["Vehicle", trip.vehicle || "—"],
    ["Driver", trip.driverName || "—"],
    ["Dispatch", trip.dispatchTime || "—"],
  ];
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.rect(margin, y, pageW - 2 * margin, 56);
  meta.forEach(([label, val], i) => {
    const cx = margin + cellW * i;
    if (i > 0) doc.line(cx, y, cx, y + 56);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text(label.toUpperCase(), cx + 10, y + 18);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    const wrapped = doc.splitTextToSize(String(val), cellW - 20);
    doc.text(wrapped, cx + 10, y + 36);
  });
  y += 68;

  // Round 97.6 — Distance + fuel row (only when we have distance data
  // from OSRM). Fuel is auto-computed at 5 km/litre (typical Adams
  // truck mileage) so ops can hand the driver a matching petrol slip.
  if (totalKm != null) {
    const cell2W = (pageW - 2 * margin) / 2;
    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(254, 249, 195); // soft amber highlight
    doc.rect(margin, y, pageW - 2 * margin, 46, "FD");
    doc.line(margin + cell2W, y, margin + cell2W, y + 46);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(120, 53, 15);
    doc.text("TOTAL DISTANCE", margin + 10, y + 16);
    doc.text("APPROX. FUEL @ 5 KM/L", margin + cell2W + 10, y + 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(17, 24, 39);
    doc.text(`${Number(totalKm).toFixed(1)} km`, margin + 10, y + 36);
    const litres = fuelLitres != null ? fuelLitres : _pdfLitresFor(trip?.vehicle, totalKm) || 0;
    doc.text(`${Number(litres).toFixed(1)} litres`, margin + cell2W + 10, y + 36);
    y += 60;
  } else {
    y += 6;
  }

  // Stops
  const stops = trip.stops || [];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text(`STOPS (${stops.length})`, margin, y);
  y += 8;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageW - margin, y);
  y += 12;

  const ensureRoom = (needed) => {
    if (y + needed > pageH - 50) {
      doc.addPage();
      y = margin;
    }
  };

  stops.forEach((s, i) => {
    const kind = s.kind === "return" ? "PICKUP" : "DELIVER";
    const o = s.order || s;
    ensureRoom(70);
    // Kind pill
    doc.setFillColor(s.kind === "return" ? 245 : 254, s.kind === "return" ? 236 : 226, s.kind === "return" ? 253 : 226);
    doc.setDrawColor(s.kind === "return" ? 147 : 220, s.kind === "return" ? 51 : 38, s.kind === "return" ? 234 : 38);
    doc.rect(margin, y - 10, 62, 16, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(s.kind === "return" ? 88 : 153, s.kind === "return" ? 28 : 27, s.kind === "return" ? 135 : 27);
    doc.text(kind, margin + 31, y + 1, { align: "center" });
    // Stop number
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.text(`${i + 1}.`, margin + 72, y + 2);
    // Customer name (bold, bigger)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(o.customer_name || "—", margin + 92, y + 2);
    y += 16;
    // Phone
    if (o.customer_phone) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      doc.text(`Phone: ${o.customer_phone}`, margin + 92, y + 2);
      y += 14;
    }
    // Address (wrap)
    if (o.address) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      const wrapped = doc.splitTextToSize(o.address, pageW - 2 * margin - 92);
      doc.text(wrapped, margin + 92, y + 2);
      y += wrapped.length * 12 + 2;
    }
    y += 12;
    // Divider
    if (i < stops.length - 1) {
      doc.setDrawColor(241, 245, 249);
      doc.line(margin, y - 6, pageW - margin, y - 6);
    }
  });

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(
    `Generated ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}  ·  ${COMPANY.web}`,
    pageW / 2,
    pageH - 24,
    { align: "center" }
  );

  const safe = String(trip.vehicle || "trip").replace(/[^a-z0-9]+/gi, "_");
  savePdf(doc, `Adams-TripSheet-${safe}-${date || "today"}.pdf`);
}


// ────────────────────────────────────────────────────────────────
// Round 97.7 — Trip Details "Actual vs Planned" Report
// One-pager for a completed trip:
//   • Vehicle · Driver · Dispatch · Return-to-park status
//   • Planned km (OSRM) · Actual km (Tracksolid GPS) · Variance
//   • Fuel used @ actual · Fuel planned @ 5 km/l
//   • Ordered list of stops + per-leg planned km
// ────────────────────────────────────────────────────────────────
export function downloadTripDetailsReport(trip, opts = {}) {
  const {
    plannedKm = null,
    actualKm = null,
    varianceKm = null,
    variancePct = null,
    returnedToPark = false,
    legs = [],
    resolved = [],
  } = opts;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  try {
    doc.addImage(ADAMS_LOGO, "JPEG", margin, 28, 130, 60);
  } catch {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(220, 38, 38);
    doc.text("ADAMS", margin, 60);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text("TRIP REPORT — ACTUAL vs PLANNED", pageW - margin, 46, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(
    new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }),
    pageW - margin,
    60,
    { align: "right" }
  );
  doc.setDrawColor(220, 38, 38);
  doc.setLineWidth(1.6);
  doc.line(margin, 98, pageW - margin, 98);

  // Vehicle / driver meta
  let y = 118;
  const cellW = (pageW - 2 * margin) / 4;
  const meta = [
    ["Vehicle", trip.vehicle_no || "—"],
    ["Driver", trip.driver_name || "Driver TBD"],
    ["Dispatch", trip.dispatch_time || "—"],
    ["Status", returnedToPark ? "AT PARK" : "ON ROAD"],
  ];
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.rect(margin, y, pageW - 2 * margin, 52);
  meta.forEach(([label, val], i) => {
    const cx = margin + cellW * i;
    if (i > 0) doc.line(cx, y, cx, y + 52);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text(label.toUpperCase(), cx + 10, y + 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    if (label === "Status") {
      doc.setTextColor(returnedToPark ? 5 : 146, returnedToPark ? 150 : 64, returnedToPark ? 105 : 14);
    } else {
      doc.setTextColor(17, 24, 39);
    }
    const wrapped = doc.splitTextToSize(String(val), cellW - 20);
    doc.text(wrapped, cx + 10, y + 34);
  });
  y += 66;

  // Distance grid — Planned, Actual, Variance
  const dCellW = (pageW - 2 * margin) / 3;
  const varTone =
    variancePct == null
      ? [17, 24, 39]
      : Math.abs(variancePct) <= 15
      ? [5, 150, 105]
      : varianceKm > 0
      ? [153, 27, 27]
      : [71, 85, 105];
  const distRows = [
    ["Planned (OSRM)", plannedKm != null ? `${Number(plannedKm).toFixed(1)} km` : "—", [180, 83, 9]],
    ["Actual (Tracksolid)", actualKm != null ? `${Number(actualKm).toFixed(1)} km` : "—", [67, 56, 202]],
    [
      "Variance",
      varianceKm != null
        ? `${varianceKm > 0 ? "+" : ""}${Number(varianceKm).toFixed(1)} km${
            variancePct != null ? ` (${variancePct > 0 ? "+" : ""}${variancePct}%)` : ""
          }`
        : "—",
      varTone,
    ],
  ];
  doc.setFillColor(249, 250, 251);
  doc.rect(margin, y, pageW - 2 * margin, 64, "FD");
  distRows.forEach(([label, val, rgb], i) => {
    const cx = margin + dCellW * i;
    if (i > 0) doc.line(cx, y, cx, y + 64);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text(label.toUpperCase(), cx + 10, y + 18);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...rgb);
    doc.text(String(val), cx + 10, y + 46);
  });
  y += 78;

  // Fuel grid — planned vs actual usage at 5 km/l
  if (plannedKm != null || actualKm != null) {
    const fCellW = (pageW - 2 * margin) / 2;
    doc.setDrawColor(245, 158, 11);
    doc.setFillColor(254, 249, 195);
    doc.rect(margin, y, pageW - 2 * margin, 46, "FD");
    doc.line(margin + fCellW, y, margin + fCellW, y + 46);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(120, 53, 15);
    const kmpl = _pdfKmplFor(trip.vehicle_no);
    doc.text(`PLANNED FUEL @ ${kmpl} KM/L`, margin + 10, y + 16);
    doc.text(`ACTUAL FUEL @ ${kmpl} KM/L`, margin + fCellW + 10, y + 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(17, 24, 39);
    doc.text(
      plannedKm != null ? `${(_pdfLitresFor(trip.vehicle_no, plannedKm) || 0).toFixed(1)} litres` : "—",
      margin + 10,
      y + 36
    );
    doc.text(
      actualKm != null ? `${(_pdfLitresFor(trip.vehicle_no, actualKm) || 0).toFixed(1)} litres` : "—",
      margin + fCellW + 10,
      y + 36
    );
    y += 60;
  }

  // Stops list
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text(`STOPS (${(trip.stops || []).length})`, margin, y);
  y += 6;
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, y, pageW - margin, y);
  y += 12;
  const ensureRoom = (needed) => {
    if (y + needed > pageH - 40) {
      doc.addPage();
      y = margin;
    }
  };
  // Build per-canonical km lookup (excluding final godown leg)
  const legKm = {};
  (legs || []).forEach((l, i) => {
    if (i === legs.length - 1) return;
    legKm[l.to_label] = l.km;
  });
  (trip.stops || []).forEach((s, i) => {
    const o = s.order || s;
    const kind = s.kind === "return" ? "PICKUP" : "DELIVER";
    ensureRoom(56);
    // Kind pill
    const isRet = s.kind === "return";
    doc.setFillColor(isRet ? 245 : 254, isRet ? 236 : 226, isRet ? 253 : 226);
    doc.rect(margin, y - 10, 62, 16, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(isRet ? 88 : 153, isRet ? 28 : 27, isRet ? 135 : 27);
    doc.text(kind, margin + 31, y + 1, { align: "center" });
    // Stop number + customer
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.text(`${i + 1}. ${o.customer_name || "—"}`, margin + 72, y + 2);
    // Look up leg km
    const rslv = (resolved || []).find(
      (r) => r.original === o.address || (r.deduped_from || []).includes(o.address)
    );
    const km = rslv ? legKm[rslv.canonical] : null;
    if (km != null) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text(`${Number(km).toFixed(1)} km`, pageW - margin, y + 2, { align: "right" });
    }
    y += 14;
    // Phone + address
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    if (o.customer_phone) {
      doc.text(o.customer_phone, margin + 72, y + 2);
      y += 12;
    }
    if (o.address) {
      const wrapped = doc.splitTextToSize(o.address, pageW - 2 * margin - 92);
      doc.text(wrapped, margin + 72, y + 2);
      y += wrapped.length * 11 + 2;
    }
    y += 8;
  });

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(
    `Actual km from Tracksolid GPS · Planned km from OSRM · Fuel estimated @ 5 km/l · ${COMPANY.web}`,
    pageW / 2,
    pageH - 24,
    { align: "center" }
  );

  const safe = String(trip.vehicle_no || "trip").replace(/[^a-z0-9]+/gi, "_");
  const stamp = new Date().toISOString().slice(0, 10);
  savePdf(doc, `Adams-TripReport-${safe}-${stamp}.pdf`);
}



// ══════════════════════════════════════════════════════════════
// ACCOUNTS ANALYSIS — Management Dashboard export (Round 106)
// One-page A4 landscape covering: KPIs, MoM, insights, category
// breakdown, monthly trend table.
// ══════════════════════════════════════════════════════════════
function _fmtINR(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `Rs. ${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `Rs. ${(v / 1e5).toFixed(2)} L`;
  return `Rs. ${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function _fmtMonthLabel(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const dt = new Date(Number(y), Number(mo) - 1, 1);
  return dt.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}


export { COMPANY, fmt as fmtMoney, amountInWords, monthLabel };
